import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useStores } from "../../../use-stores";
import { observer } from "mobx-react";
import {
  Box,
  Container,
  Paper,
  Chip,
  Grid2,
  Card,
  CardContent,
  CircularProgress,
  useTheme,
  IconButton,
  Icon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Popover,
  MenuItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
} from "@mui/material";
import {
  Download as DownloadIcon,
  ShoppingCart as ShoppingCartIcon,
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  AccountBalance as AccountBalanceIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  Calculate as CalculateIcon,
  ExpandMore as ExpandMoreIcon,
  Receipt as ReceiptIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  PersonOutline as PersonOutlineIcon,
  Update as UpdateIcon,
} from "@mui/icons-material";
import {
  PurchaseRequestProduct,
  PurchaseRequestProductInsights,
} from "../../../models/PurchaseRequest/PurchaseRequestProductInsights";
import CustomTypography from "../../components/common/form/CustomTypography";
import AppButton from "../../components/common/buttons/AppButton";
import { useNotification } from "../../../utils/NotificationsContext";
import { formatDate } from "../../components/common/functions/formatDate";
import SingleInput from "../../components/common/pop-up/SingleInput";
import { GstPercentage } from "../../../models/Order/OrderStatus";
import pencil_uderline from "../../../assets/pencil_uderline.png";
import AppDropDown from "../../components/common/filter/AppDropDown";
import ConfirmationPopup from "../../components/common/pop-up/ConfirmationPopup";
import {
  PurchaseOrderStatusOptions,
  PurchaseRequestRejectedBy,
} from "../../../models/PurchaseRequest/PurchaseRequestBody";
import PurchaseVouchersPopup from "../../components/purchase-request/PurchaseVouchersPopup";
import {
  PurchaseOrderInvoice,
  InvoiceUploadData,
} from "../../../models/Invoice/PurchaseOrderInvoice";
import { InvoicePaymentStatusFilters } from "../../../models/ProjectManagement/ProjectInventoryProduct";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import InvoiceUploadDialog from "../../components/purchase-request/InvoiceUploadDialog";
import VendorPaymentPopup from "./VendorPaymentPopup";
import { VendorPaymentRow } from "../../../models/PurchaseRequest/VendorPayments";
import { VendorPaymentsStore } from "../../../stores/VendorPaymentsStore";
import {
  canEditPrProductFields,
  canReactivatePurchaseOrder,
  isPoCancelledOrClosed,
} from "../../../utils/purchaseRequestPoHelpers";

const vendorPaymentsStore = new VendorPaymentsStore();

function vendorPaymentActorLabel(
  name: string | null | undefined,
  userId: number | null | undefined,
): string {
  const n = name?.trim();
  if (n) return n;
  if (userId != null) return `User #${userId}`;
  return "—";
}

/** True when the row was edited after creation (avoids duplicating create + update on first insert). */
function vendorPaymentWasEdited(payment: VendorPaymentRow): boolean {
  if (payment.updated_by === null) return false;
  if (payment.updated_by !== payment.created_by) return true;
  const created = payment.created_at
    ? new Date(payment.created_at).getTime()
    : 0;
  const updated = payment.updated_at
    ? new Date(payment.updated_at).getTime()
    : 0;
  return updated > created + 2000;
}

const PurchaseRequestDetail = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const prId = searchParams.get("prId");
  const projectId = searchParams.get("projectId");
  const theme = useTheme();
  const {
    purchaseRequestStore,
    projectSourcingOpsStore,
    invoiceStore,
    userStore,
  } = useStores();
  const { showSuccess, showError } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [purchaseVouchersPopupOpen, setPurchaseVouchersPopupOpen] =
    useState(false);
  const [selectedProductForVouchers, setSelectedProductForVouchers] =
    useState<PurchaseRequestProduct | null>(null);
  const [paymentStatusAnchor, setPaymentStatusAnchor] = useState<{
    element: HTMLElement;
    invoiceId: number;
  } | null>(null);
  const [invoiceDetailsPopup, setInvoiceDetailsPopup] = useState<{
    open: boolean;
    invoice: PurchaseOrderInvoice | null;
  }>({ open: false, invoice: null });

  // Sourcing approve/reject actions live on the details page (not in the listing)
  const [sourcingApprovePopupOpen, setSourcingApprovePopupOpen] =
    useState(false);
  const [sourcingRejectPopupOpen, setSourcingRejectPopupOpen] = useState(false);

  // Finance approve/reject actions live on the details page (not in the listing)
  const [financeApprovePopupOpen, setFinanceApprovePopupOpen] = useState(false);
  const [financeRejectPopupOpen, setFinanceRejectPopupOpen] = useState(false);

  // Swageazy approve/reject actions live on the details page (not in the listing)
  const [swageazyApprovePopupOpen, setSwageazyApprovePopupOpen] =
    useState(false);
  const [swageazyRejectPopupOpen, setSwageazyRejectPopupOpen] = useState(false);

  // Cancel PR action live on the details page (not in the listing)
  const [cancelPRPopupOpen, setCancelPRPopupOpen] = useState(false);
  const [reactivatePoPopupOpen, setReactivatePoPopupOpen] = useState(false);
  const [reactivatePoId, setReactivatePoId] = useState<number | null>(null);

  useEffect(() => {
    if (prId && projectId) {
      setIsLoading(true);
      purchaseRequestStore
        .getPurchaseRequestDetail(Number(prId))
        .then(() => {
          const productIds =
            purchaseRequestStore.purchaseRequestDetail?.products?.map(
              (product) => product.product_id,
            );
          if (productIds) {
            if (
              Number(projectId) > 0 &&
              userStore.hasPermission(EmployeeRoles.VIEW_COST_PRICE)
            ) {
              projectSourcingOpsStore
                .getPurchaseRequestProductsInsight(
                  Number(projectId),
                  productIds,
                )
                .catch((error) => {
                  showError(
                    "Error",
                    "Failed to fetch purchase request product insights, please try again later",
                  );
                });
            }
            purchaseRequestStore
              .getPrProductAvailableQty(productIds, Number(projectId))
              .then(() => {
                setIsLoading(false);
              });
          }
        })
        .catch(() => {
          setIsLoading(false);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prId, projectId]);

  useEffect(() => {
    if (!prId || !userStore.hasPermission(EmployeeRoles.VIEW_VENDOR_PAYMENTS))
      return;
    vendorPaymentsStore.getVendorPayments(Number(prId)).catch(() => {
      showError(
        "Error",
        "Failed to fetch vendor payments. Please try again later.",
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prId, userStore]);

  const handleDownloadPO = (po_id: number) => {
    purchaseRequestStore
      .downloadPurchaseOrder(po_id)
      .then((response) => {
        if (response.data) {
          const downloadUrl = window.URL.createObjectURL(response.data);
          const a = document.createElement("a");
          a.href = downloadUrl;
          a.download = response.filename
            ? response.filename
            : `PO_${po_id}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(downloadUrl);
        } else {
          return showError("Error Occurred", "Failed to download PO.");
        }
      })
      .catch(async (err) => {
        const json = JSON.parse(await err?.response?.data?.text());
        showError(
          "Error",
          json.message || "Something went wrong! Please try again later.",
        );
      });
  };

  const handleSendEmail = (po_id: number) => {
    if (!prId) return;
    purchaseRequestStore
      .sendEmailToVendor(Number(prId), po_id)
      .then(() => {
        showSuccess(
          "Purchase order email has been sent to the vendor successfully.",
        );
      })
      .catch((error: any) => {
        showError(
          "Error Occurred",
          error?.response?.data?.message ||
          "Failed to send email, please try again later",
        );
      });
  };

  const handleStatusChange = (po_id: number, status: string) => {
    if (status === "CANCELLED") {
      purchaseRequestStore.openCancellationModal(po_id);
    } else {
      purchaseRequestStore.openStatusChangeModal(po_id, status);
    }
  };

  const updatePOStatus = (
    po_id: number,
    status: string,
    cancellationReason?: string,
  ) => {
    purchaseRequestStore
      .updatePOStatus(po_id, status, cancellationReason)
      .then((response) => {
        if (response.success) {
          showSuccess(`PO status updated to ${status} successfully.`);
          if (status === "CANCELLED") {
            const productIds =
              purchaseRequestStore.purchaseRequestDetail?.products?.map(
                (product) => product.product_id,
              );
            if (productIds?.length && projectId) {
              purchaseRequestStore.getPrProductAvailableQty(
                productIds,
                Number(projectId),
              );
            }
          }
        }
      })
      .catch((error: any) => {
        showError(
          "Error Occurred",
          error?.response?.data?.message ||
          "Failed to update PO status, please try again later",
        );
      });
  };

  const handleCancellationSubmit = (id: number, input?: string) => {
    if (!input || !input.trim()) {
      showError("Error", "Please enter a cancellation reason");
      return;
    }

    updatePOStatus(id, "CANCELLED", input);
    purchaseRequestStore.closeCancellationModal();
  };

  const handleCancellationClose = () => {
    purchaseRequestStore.closeCancellationModal();
  };

  const handleStatusChangeConfirm = (id: number) => {
    const status = purchaseRequestStore.statusChangeModal.status;
    if (status) {
      updatePOStatus(id, status);
      purchaseRequestStore.closeStatusChangeModal();
    }
  };

  const handleStatusChangeClose = () => {
    purchaseRequestStore.closeStatusChangeModal();
  };

  const handleApproveSourcingConfirm = (id: number) => {
    purchaseRequestStore
      .approveRequest(id, PurchaseRequestRejectedBy.SOURCING, userStore)
      .then(() => {
        showSuccess("Sourcing approved successfully");
        setSourcingApprovePopupOpen(false);
        return purchaseRequestStore.getPurchaseRequestDetail(id);
      })
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
          "Failed to approve sourcing. Please try again later.",
        );
      });
  };

  const handleApproveSourcingClose = () => {
    setSourcingApprovePopupOpen(false);
  };

  const handleRejectSourcingConfirm = (
    id: number,
    rejectionReason?: string,
    _taskAssigned?: boolean,
  ) => {
    if (!rejectionReason || !rejectionReason.trim()) {
      showError("Error", "Please enter a rejection reason");
      return;
    }

    purchaseRequestStore
      .rejectRequest(
        id,
        PurchaseRequestRejectedBy.SOURCING,
        userStore,
        rejectionReason,
      )
      .then(() => {
        showSuccess("Sourcing rejected successfully");
        setSourcingRejectPopupOpen(false);
        return purchaseRequestStore.getPurchaseRequestDetail(id);
      })
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
          "Failed to reject sourcing. Please try again later.",
        );
      });
  };

  const handleRejectSourcingClose = () => {
    setSourcingRejectPopupOpen(false);
  };

  const handleApproveFinanceConfirm = (id: number) => {
    purchaseRequestStore
      .approveRequest(id, PurchaseRequestRejectedBy.FINANCE, userStore)
      .then(() => {
        showSuccess("Finance approved successfully");
        setFinanceApprovePopupOpen(false);
        return purchaseRequestStore.getPurchaseRequestDetail(id);
      })
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
          "Failed to approve finance. Please try again later.",
        );
      });
  };

  const handleApproveFinanceClose = () => {
    setFinanceApprovePopupOpen(false);
  };

  const handleRejectFinanceConfirm = (
    id: number,
    rejectionReason?: string,
    _taskAssigned?: boolean,
  ) => {
    if (!rejectionReason || !rejectionReason.trim()) {
      showError("Error", "Please enter a rejection reason");
      return;
    }

    purchaseRequestStore
      .rejectRequest(
        id,
        PurchaseRequestRejectedBy.FINANCE,
        userStore,
        rejectionReason,
      )
      .then(() => {
        showSuccess("Finance rejected successfully");
        setFinanceRejectPopupOpen(false);
        return purchaseRequestStore.getPurchaseRequestDetail(id);
      })
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
          "Failed to reject finance. Please try again later.",
        );
      });
  };

  const handleRejectFinanceClose = () => {
    setFinanceRejectPopupOpen(false);
  };

  const handleApproveSwageazyConfirm = (id: number) => {
    purchaseRequestStore
      .approveRequest(id, PurchaseRequestRejectedBy.SWAGEAZY, userStore)
      .then(() => {
        showSuccess("Swageazy approved successfully");
        setSwageazyApprovePopupOpen(false);
        return purchaseRequestStore.getPurchaseRequestDetail(id);
      })
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
          "Failed to approve swageazy. Please try again later.",
        );
      });
  };

  const handleApproveSwageazyClose = () => {
    setSwageazyApprovePopupOpen(false);
  };

  const handleRejectSwageazyConfirm = (
    id: number,
    rejectionReason?: string,
    _taskAssigned?: boolean,
  ) => {
    if (!rejectionReason || !rejectionReason.trim()) {
      showError("Error", "Please enter a rejection reason");
      return;
    }

    purchaseRequestStore
      .rejectRequest(
        id,
        PurchaseRequestRejectedBy.SWAGEAZY,
        userStore,
        rejectionReason,
      )
      .then(() => {
        showSuccess("Swageazy rejected successfully");
        setSwageazyRejectPopupOpen(false);
        return purchaseRequestStore.getPurchaseRequestDetail(id);
      })
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
          "Failed to reject swageazy. Please try again later.",
        );
      });
  };

  const handleRejectSwageazyClose = () => {
    setSwageazyRejectPopupOpen(false);
  };

  const handleCancelPRClick = () => {
    if (!purchaseRequestStore.purchaseRequestDetail) return;

    // if (
    //   (purchaseRequestStore.purchaseRequestDetail.purchase_orders?.length || 0) >
    //   0
    // ) {
    //   showError(
    //     "Error",
    //     "Unable to cancel the PR because a PO has already been generated.",
    //   );
    //   return;
    // }

    if (purchaseRequestStore.purchaseRequestDetail.active_po === 1) {
      showError(
        "Error",
        "Unable to cancel the PR because it has an active PO assigned. Please close the PO before proceeding with the PR cancellation.",
      );
      return;
    }

    if (
      (purchaseRequestStore.purchaseRequestDetail.purchase_order_invoices
        ?.length ?? 0) > 0
    ) {
      showError(
        "Error",
        "Unable to cancel the PR because a vendor invoice has already been uploaded.",
      );
      return;
    }

    setCancelPRPopupOpen(true);
  };

  const handleCancelPRConfirm = (
    id: number,
    cancellationReason?: string,
    _taskAssigned?: boolean,
  ) => {
    if (!cancellationReason || !cancellationReason.trim()) {
      showError("Error", "Please enter a cancellation reason");
      return;
    }

    purchaseRequestStore
      .cancelPurchaseRequest(id, userStore, cancellationReason)
      .then(() => {
        showSuccess("Request cancelled successfully");
        setCancelPRPopupOpen(false);
        if (prId) {
          return purchaseRequestStore.getPurchaseRequestDetail(Number(prId));
        }
      })
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
          "Failed to cancel the request. Please try again later.",
        );
      });
  };

  const handleCancelPRClose = () => {
    setCancelPRPopupOpen(false);
  };

  const handleOpenReactivatePo = (poId: number) => {
    setReactivatePoId(poId);
    setReactivatePoPopupOpen(true);
  };

  const handleReactivatePoClose = () => {
    setReactivatePoPopupOpen(false);
    setReactivatePoId(null);
  };

  const handleReactivatePOConfirm = (poId: number) => {
    const prIdNum = prId
      ? Number(prId)
      : purchaseRequestStore.purchaseRequestDetail?.id;
    if (!prIdNum || !poId) return;

    purchaseRequestStore
      .reactivatePO(prIdNum, poId)
      .then(() => {
        showSuccess("Purchase order reactivated successfully");
        handleReactivatePoClose();
        const productIds =
          purchaseRequestStore.purchaseRequestDetail?.products?.map(
            (product) => product.product_id,
          );
        if (productIds?.length && projectId) {
          purchaseRequestStore.getPrProductAvailableQty(
            productIds,
            Number(projectId),
          );
        }
      })
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
          "Failed to reactivate purchase order. Please try again later.",
        );
      });
  };

  const handleGeneratePO = () => {
    if (!purchaseRequestStore.purchaseRequestDetail) return;
    const detail = purchaseRequestStore.purchaseRequestDetail;

    purchaseRequestStore
      .generatePO(detail.id)
      .then(() => {
        showSuccess(
          detail.po_id > 0
            ? "PO regenerated successfully"
            : "PO generated successfully",
        );
        const productIds = detail.products?.map((product) => product.product_id);
        if (productIds?.length && projectId) {
          purchaseRequestStore.getPrProductAvailableQty(
            productIds,
            Number(projectId),
          );
        }
        return purchaseRequestStore.getPurchaseRequestDetail(detail.id);
      })
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
          "Failed to generate PO. Please try again later.",
        );
      });
  };

  const handleApproveInvoice = (invoiceId: number) => {
    invoiceStore.openApprovePurchaseOrderInvoicePopup(invoiceId);
  };

  const handleRejectInvoice = (invoiceId: number) => {
    invoiceStore.openRejectPurchaseOrderInvoicePopup(invoiceId);
  };

  const handleApproveInvoiceConfirm = (invoiceId: number) => {
    invoiceStore
      .approvePurchaseOrderInvoiceRequest(invoiceId)
      .then(() => {
        showSuccess("Invoice approved successfully");
        invoiceStore.closeApprovePurchaseOrderInvoicePopup();
        if (prId) {
          return purchaseRequestStore.getPurchaseRequestDetail(Number(prId));
        }
      })
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
          "Failed to approve invoice. Please try again later.",
        );
      });
  };

  const handleRejectInvoiceConfirm = (
    invoiceId: number,
    rejectionReason?: string,
  ) => {
    invoiceStore
      .rejectPurchaseOrderInvoiceRequest(invoiceId, rejectionReason)
      .then(() => {
        showSuccess("Invoice rejected successfully");
        invoiceStore.closeRejectPurchaseOrderInvoicePopup();
        if (prId) {
          return purchaseRequestStore.getPurchaseRequestDetail(Number(prId));
        }
      })
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
          "Failed to reject invoice. Please try again later.",
        );
      });
  };

  const handleRejectInvoiceClose = () => {
    invoiceStore.closeRejectPurchaseOrderInvoicePopup();
  };

  const handleApproveInvoiceClose = () => {
    invoiceStore.closeApprovePurchaseOrderInvoicePopup();
  };

  const handleMarkPaymentStatus = (
    invoiceId: number,
    paymentStatus: string,
  ) => {
    invoiceStore
      .markPurchaseOrderInvoicePaymentStatus(invoiceId, paymentStatus)
      .then(() => {
        showSuccess("Payment status updated successfully");
        setPaymentStatusAnchor(null);
        if (prId) {
          return purchaseRequestStore.getPurchaseRequestDetail(Number(prId));
        }
      })
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
          "Failed to update payment status. Please try again later.",
        );
      });
  };

  const handlePaymentStatusClick = (
    event: React.MouseEvent<HTMLElement>,
    invoiceId: number,
  ) => {
    setPaymentStatusAnchor({ element: event.currentTarget, invoiceId });
  };

  const handlePaymentStatusClose = () => {
    setPaymentStatusAnchor(null);
  };

  const handlePaymentStatusSelect = (statusId: string) => {
    if (paymentStatusAnchor) {
      handleMarkPaymentStatus(paymentStatusAnchor.invoiceId, statusId);
    }
  };

  const handleOpenInvoiceUpload = (poId: number) => {
    if (!userStore.hasPermission(EmployeeRoles.UPLOAD_PURCHASE_ORDER_INVOICE)) {
      showError(
        "Access Denied",
        "You don't have permission to upload purchase order invoices.",
      );
      return;
    }
    const vendorId = purchaseRequestStore.purchaseRequestDetail?.vendor_id || 0;
    invoiceStore.openInvoiceUploadDialog(poId, vendorId);
  };

  const handleCloseInvoiceUpload = () => {
    invoiceStore.closeInvoiceUploadDialog();
  };

  const handleInvoiceUpload = async (uploadData: InvoiceUploadData) => {
    try {
      const response = await invoiceStore.invoiceAPI.uploadInvoice({
        po_id: uploadData.po_id,
        vendor_id: uploadData.vendor_id,
        invoice_url: uploadData.invoice_url,
        invoice_number: uploadData.invoice_number,
        invoice_amount: uploadData.invoice_amount,
        invoice_date: uploadData.invoice_date,
        invoice_items: uploadData.invoice_items || [],
        ...(uploadData.other_charges !== undefined && {
          other_charges: uploadData.other_charges,
        }),
      } as any);

      if (response.success) {
        showSuccess(response.message || "Invoice uploaded successfully");
        handleCloseInvoiceUpload();
        if (prId) {
          return purchaseRequestStore.getPurchaseRequestDetail(Number(prId));
        }
      } else {
        showError("Error", response.message || "Failed to upload invoice");
      }
    } catch (error: any) {
      showError(
        "Error",
        error?.response?.data?.message ||
        error?.message ||
        "Failed to upload invoice. Please try again later.",
      );
    }
  };

  const formatPaymentTimingLabel = (value: string) => {
    if (value === "post_invoice") return "Post Invoice";
    if (value === "advance") return "Advance";
    return value || "-";
  };

  const formatPaymentModeLabel = (mode: string | null | undefined) => {
    if (!mode?.trim()) return "Mode not provided";
    const labels: Record<string, string> = {
      check: "Cheque",
      razorpay: "Razorpay",
      stripe: "Stripe",
      creditcard: "Credit card",
      banktransfer: "Bank transfer",
      bankremittance: "Bank remittance",
      autotransaction: "Auto transaction",
      others: "Others",
    };
    const key = mode.trim();
    return labels[key] ?? key;
  };

  const totalPaidAmount = vendorPaymentsStore.payments.reduce(
    (sum, payment) => {
      const amount = Number(String(payment.amount ?? 0).replace(/,/g, ""));
      return Number.isNaN(amount) ? sum : sum + amount;
    },
    0,
  );
  const currentGrandTotalAmount = (() => {
    const products = purchaseRequestStore.purchaseRequestDetail?.products || [];
    const subtotal = products.reduce(
      (sum, product) => sum + (product.rate || 0) * (product.quantity || 0),
      0,
    );
    const totalGst = products.reduce((sum, product) => {
      const productTotal = (product.rate || 0) * (product.quantity || 0);
      return sum + (productTotal * (product.gst_percentage || 0)) / 100;
    }, 0);
    const otherCharges =
      purchaseRequestStore.purchaseRequestDetail?.other_charges || 0;
    return Math.round((subtotal + totalGst + otherCharges) * 100) / 100;
  })();
  const editingPaymentAmountForPopup = Number(
    String(vendorPaymentsStore.popupState.payment?.amount ?? 0).replace(
      /,/g,
      "",
    ),
  );
  const adjustedEditingAmountForPopup = Number.isNaN(
    editingPaymentAmountForPopup,
  )
    ? 0
    : editingPaymentAmountForPopup;
  const maxAllowedPaymentAmount =
    Math.max(
      0,
      currentGrandTotalAmount -
      (totalPaidAmount - adjustedEditingAmountForPopup),
    ) + 1;

  const handleOpenCreateVendorPayment = () => {
    if (!userStore.hasPermission(EmployeeRoles.ADD_EDIT_VENDOR_PAYMENTS))
      return;
    vendorPaymentsStore.openCreatePopup();
  };

  const handleOpenEditVendorPayment = (payment: VendorPaymentRow) => {
    if (!userStore.hasPermission(EmployeeRoles.ADD_EDIT_VENDOR_PAYMENTS))
      return;
    vendorPaymentsStore.openEditPopup(payment);
  };

  const handleSaveVendorPayment = async (payload: {
    amount: number;
    payment_timing: "advance" | "post_invoice";
    payment_mode: string | null;
    remarks: string | null;
  }) => {
    if (
      !prId ||
      !userStore.hasPermission(EmployeeRoles.ADD_EDIT_VENDOR_PAYMENTS)
    )
      return;
    try {
      const existingPaidAmount = vendorPaymentsStore.payments.reduce(
        (sum, payment) => {
          const amount = Number(String(payment.amount ?? 0).replace(/,/g, ""));
          return Number.isNaN(amount) ? sum : sum + amount;
        },
        0,
      );
      const editingPaymentAmount = Number(
        String(vendorPaymentsStore.popupState.payment?.amount ?? 0).replace(
          /,/g,
          "",
        ),
      );
      const adjustedEditingAmount = Number.isNaN(editingPaymentAmount)
        ? 0
        : editingPaymentAmount;
      const nextTotalPaid =
        existingPaidAmount -
        adjustedEditingAmount +
        Number(payload.amount || 0);
      const grandTotalAmount = currentGrandTotalAmount;

      if (nextTotalPaid > grandTotalAmount + 1) {
        throw new Error(
          `Total paid amount cannot exceed grand total (₹${grandTotalAmount.toLocaleString("en-IN")}).`,
        );
      }

      await vendorPaymentsStore.saveVendorPayment(
        { ...payload, pr_id: Number(prId) },
        vendorPaymentsStore.popupState.payment?.id,
      );
      await vendorPaymentsStore.getVendorPayments(Number(prId));
      vendorPaymentsStore.closePopup();
      showSuccess(
        vendorPaymentsStore.popupState.payment
          ? "Vendor payment updated successfully."
          : "Vendor payment added successfully.",
      );
    } catch (error: any) {
      throw new Error(
        error?.response?.data?.message ||
        "Failed to save vendor payment. Please try again later.",
      );
    }
  };

  const handleDeleteVendorPayment = (paymentId: number) => {
    if (!userStore.hasPermission(EmployeeRoles.DELETE_VENDOR_PAYMENTS)) return;
    vendorPaymentsStore.openDeletePopup(paymentId);
  };

  const handleConfirmDeleteVendorPayment = async (id: number) => {
    if (!userStore.hasPermission(EmployeeRoles.DELETE_VENDOR_PAYMENTS)) return;
    try {
      await vendorPaymentsStore.deleteVendorPayment(id);
      showSuccess("Vendor payment deleted successfully.");
      vendorPaymentsStore.closeDeletePopup();
    } catch (error: any) {
      showError(
        "Error",
        error?.response?.data?.message ||
        "Failed to delete vendor payment. Please try again later.",
      );
    }
  };

  const getStatusIcon = (approval: number, rejectionDate: string | null) => {
    if (rejectionDate) {
      return <CancelIcon sx={{ color: "error.main", fontSize: 24 }} />;
    }
    if (approval === 1) {
      return <CheckCircleIcon sx={{ color: "success.main", fontSize: 24 }} />;
    }
    return <PendingIcon sx={{ color: "warning.main", fontSize: 24 }} />;
  };

  const handleEditProduct = (
    product: PurchaseRequestProduct,
    field: string,
  ) => {
    const detail = purchaseRequestStore.purchaseRequestDetail;
    if (!detail) return;

    const hasPostPoEditPermission = userStore.hasPermission(
      EmployeeRoles.EDIT_PR_AND_PO_AFTER_PO_GENERATION,
      true,
    );
    if (!canEditPrProductFields(detail, hasPostPoEditPermission)) {
      showError(
        "Access Denied",
        detail.po_id > 0
          ? "Cancel the purchase order first. Only authorized users can edit the PR after PO cancellation."
          : "This purchase request cannot be edited.",
      );
      return;
    }

    purchaseRequestStore.openEditDialog(product.product_id, field);
  };

  const handleModalSubmit = (
    productId: number,
    value: string | number,
    field: string,
  ) => {
    handleUpdateProductDetails(productId, value, field);
  };

  const handleUpdateProductDetails = (
    id: number,
    value: string | number,
    field: string,
  ) => {
    setIsLoading(true);
    const product = purchaseRequestStore.purchaseRequestDetail?.products?.find(
      (p) => p.product_id === id,
    );

    if (product) {
      purchaseRequestStore
        .updateProductField(
          product,
          value,
          field,
          projectId ? Number(projectId) : undefined,
        )
        .then(() => {
          showSuccess("Product details updated successfully");
          if (prId) {
            return purchaseRequestStore.getPurchaseRequestDetail(Number(prId));
          }
        })
        .catch((err) => {
          const errorMessage =
            err?.response?.data?.message || err.response?.data?.message;
          showError("Error Occurred", errorMessage);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  if (isLoading) {
    return (
      <Container
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (!purchaseRequestStore.purchaseRequestDetail) {
    return (
      <Container>
        <CustomTypography variant="heading6" color="error">
          Purchase Request not found
        </CustomTypography>
      </Container>
    );
  }

  const prDetail = purchaseRequestStore.purchaseRequestDetail;
  const isPRCancelled = Boolean(prDetail.pr_cancellation_date);
  const isAnyApprovalRejected =
    (Number(prDetail.rejected_by_finance_admin_id) || 0) > 0 ||
    (Number(prDetail.rejected_by_sourcing_admin_id) || 0) > 0 ||
    (Number(prDetail.rejected_by_swageazy_admin_id) || 0) > 0;

  const hasPostPoEditPermission = userStore.hasPermission(
    EmployeeRoles.EDIT_PR_AND_PO_AFTER_PO_GENERATION,
    false,
  );
  const showProductEditIcon = canEditPrProductFields(
    prDetail,
    hasPostPoEditPermission,
  );
  const showReactivatePoAction = canReactivatePurchaseOrder(
    prDetail,
    hasPostPoEditPermission,
  );

  const formatFieldName = (fieldName: string): string => {
    return fieldName
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/^./, (str) => str?.toUpperCase());
  };

  const calculateTotals = () => {
    if (!purchaseRequestStore.purchaseRequestDetail?.products) {
      return {
        subtotal: 0,
        totalGst: 0,
        otherCharges: 0,
        grandTotal: 0,
      };
    }

    let subtotal = 0;
    let totalGst = 0;

    purchaseRequestStore.purchaseRequestDetail.products.forEach((product) => {
      const productTotal = (product.rate || 0) * (product.quantity || 0);
      subtotal += productTotal;

      const gstAmount = (productTotal * (product.gst_percentage || 0)) / 100;
      totalGst += gstAmount;
    });

    const otherCharges =
      purchaseRequestStore.purchaseRequestDetail.other_charges || 0;
    const grandTotal = subtotal + totalGst + otherCharges;

    return {
      subtotal: Math.round(subtotal * 100) / 100,
      totalGst: Math.round(totalGst * 100) / 100,
      otherCharges,
      grandTotal: Math.round(grandTotal * 100) / 100,
    };
  };

  const totals = calculateTotals();
  const remainingGap = Number(totals?.grandTotal || 0) - totalPaidAmount;
  const remainingAmountToBePaid = remainingGap < 1 ? 0 : remainingGap;

  const getFilteredInvoices = () => {
    if (!purchaseRequestStore.purchaseRequestDetail?.purchase_order_invoices) {
      return [];
    }
    return purchaseRequestStore.purchaseRequestDetail.purchase_order_invoices.filter(
      (invoice: PurchaseOrderInvoice) => {
        if (purchaseRequestStore.prFilter === "ALL") return true;
        if (purchaseRequestStore.prFilter === "PENDING") {
          return (
            !invoice.invoice_status ||
            invoice.invoice_status === null ||
            invoice.invoice_status === ""
          );
        }
        return invoice.invoice_status === purchaseRequestStore.prFilter;
      },
    );
  };

  const filteredInvoices = getFilteredInvoices();

  const calculateInvoiceStats = () => {
    const invoices =
      purchaseRequestStore.purchaseRequestDetail?.purchase_order_invoices || [];

    let totalUploaded = 0;
    let approved = 0;
    let pending = 0;
    let rejected = 0;
    let otherCharges = 0;

    invoices.forEach((invoice: PurchaseOrderInvoice) => {
      const invoiceAmount = Number(invoice.invoice_amount) || 0;
      const charges = Number(invoice.other_charges) || 0;

      totalUploaded += invoiceAmount;
      otherCharges += charges;

      if (
        invoice.approve_invoice !== null &&
        invoice.approve_invoice !== undefined
      ) {
        approved += invoiceAmount;
      } else if (
        invoice.reject_invoice !== null &&
        invoice.reject_invoice !== undefined
      ) {
        rejected += invoiceAmount;
      } else {
        pending += invoiceAmount;
      }
    });

    const round = (amount: number) => Math.round(amount * 100) / 100;

    return {
      totalUploaded: round(totalUploaded),
      approved: round(approved),
      pending: round(pending),
      rejected: round(rejected),
      otherCharges: round(otherCharges),
    };
  };

  const invoiceStats = calculateInvoiceStats();

  const formatCurrency = (amount: number) => {
    return `₹${amount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header with Go Back Button */}
      <Box
        sx={{
          mb: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <AppButton
          label="Go back"
          variant="transparentPrimary"
          startIcon={<ArrowBackIcon />}
          onClick={() => window.history.back()}
        />
      </Box>

      {/* Section 1: Purchase Request Details */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 3,
            p: 2,
            backgroundColor: theme.palette.primary.main + "08",
            borderRadius: 2,
            border: `2px solid ${theme.palette.primary.main}20`,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              backgroundColor: theme.palette.primary.main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <BusinessIcon sx={{ color: "white", fontSize: 20 }} />
          </Box>
          <Box>
            <CustomTypography
              variant="heading5"
              fontWeight={700}
              color="primary.main"
            >
              Purchase Request Details
            </CustomTypography>
            <CustomTypography variant="body2" color="text.secondary">
              Basic information and approval status
            </CustomTypography>
          </Box>
        </Box>

        <Paper
          sx={{
            p: 4,
            background: theme.palette.customColors.white,
            borderRadius: 3,
            boxShadow: "0px 4px 20px 0px rgba(0, 0, 0, 0.08)",
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          {/* Main Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 4,
              flexWrap: "wrap",
              gap: 3,
            }}
          >
            <Box sx={{ flex: 1, minWidth: "300px" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 2,
                  flexWrap: "wrap",
                }}
              >
                <CustomTypography
                  variant="heading3"
                  fontWeight={700}
                  color="primary.main"
                >
                  Purchase Request #
                  {purchaseRequestStore.purchaseRequestDetail.id}
                </CustomTypography>
                <Chip
                  label={`Order #${purchaseRequestStore.purchaseRequestDetail.order_id}`}
                  color="primary"
                  variant="filled"
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    height: "32px",
                  }}
                />
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <CustomTypography variant="body1" color="text.secondary">
                  <strong>Created:</strong>{" "}
                  {formatDate(
                    purchaseRequestStore.purchaseRequestDetail.created_date,
                  )}
                </CustomTypography>
                <CustomTypography variant="body1" color="text.secondary">
                  <strong>Created by:</strong>{" "}
                  {
                    purchaseRequestStore.purchaseRequestDetail
                      .created_by_adminname
                  }
                </CustomTypography>
                <CustomTypography variant="body1" color="text.secondary">
                  <strong>Expected Delivery Date:</strong>{" "}
                  {formatDate(
                    purchaseRequestStore.purchaseRequestDetail
                      .expected_delivery_date,
                  )}
                </CustomTypography>
              </Box>
            </Box>

            {/* Approval Status Cards */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              {[
                {
                  label: "Finance",
                  approval:
                    purchaseRequestStore.purchaseRequestDetail.finance_approval,
                  rejectionDate:
                    purchaseRequestStore.purchaseRequestDetail
                      .rejection_date_by_finance,
                  color: theme.palette.info.main,
                },
                {
                  label: "Sourcing",
                  approval:
                    purchaseRequestStore.purchaseRequestDetail
                      .sourcing_approval,
                  rejectionDate:
                    purchaseRequestStore.purchaseRequestDetail
                      .rejection_date_by_sourcing,
                  color: theme.palette.warning.main,
                },
                {
                  label: "Swageazy",
                  approval:
                    purchaseRequestStore.purchaseRequestDetail
                      .swageazy_approval,
                  rejectionDate:
                    purchaseRequestStore.purchaseRequestDetail
                      .rejection_date_by_swageazy,
                  color: theme.palette.success.main,
                },
              ].map((status, index) => (
                <Box
                  key={status.label}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 2,
                    border: `2px solid ${status.color}20`,
                    borderRadius: 2,
                    bgcolor: `${status.color}08`,
                    minWidth: 120,
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      bgcolor: `${status.color}15`,
                      transform: "translateY(-2px)",
                      boxShadow: `0px 4px 12px 0px ${status.color}30`,
                    },
                  }}
                >
                  <Box sx={{ mb: 1 }}>
                    {getStatusIcon(status.approval, status.rejectionDate)}
                  </Box>
                  <CustomTypography
                    variant="body2"
                    color="text.primary"
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 600,
                    }}
                  >
                    {status.label}
                  </CustomTypography>

                  {status.label === "Sourcing" &&
                    userStore.hasPermission(
                      EmployeeRoles.SOURCING_TEAM_PR_ACTIONS,
                    ) &&
                    Boolean(prDetail.sourcing_approval) === false &&
                    (prDetail.rejected_by_sourcing_admin_id === null ||
                      prDetail.rejected_by_sourcing_admin_id <= 0) && (
                      <Box sx={{ mt: 1.5, display: "flex", gap: 1 }}>
                        <AppButton
                          label="Approve"
                          variant="primary"
                          onClick={() => setSourcingApprovePopupOpen(true)}
                          disabled={isPRCancelled || isAnyApprovalRejected}
                          customStyles={{ minWidth: 100, height: 36 }}
                        />
                        <AppButton
                          label="Reject"
                          variant="secondary"
                          onClick={() => setSourcingRejectPopupOpen(true)}
                          disabled={isPRCancelled || isAnyApprovalRejected}
                          customStyles={{ minWidth: 100, height: 36 }}
                        />
                      </Box>
                    )}

                  {status.label === "Finance" &&
                    userStore.hasPermission(
                      EmployeeRoles.FINANCE_TEAM_PR_ACTIONS,
                    ) &&
                    Boolean(prDetail.finance_approval) === false &&
                    (prDetail.rejected_by_finance_admin_id === null ||
                      prDetail.rejected_by_finance_admin_id <= 0) && (
                      <Box sx={{ mt: 1.5, display: "flex", gap: 1 }}>
                        <AppButton
                          label="Approve"
                          variant="primary"
                          onClick={() => setFinanceApprovePopupOpen(true)}
                          disabled={isPRCancelled || isAnyApprovalRejected}
                          customStyles={{ minWidth: 100, height: 36 }}
                        />
                        <AppButton
                          label="Reject"
                          variant="secondary"
                          onClick={() => setFinanceRejectPopupOpen(true)}
                          disabled={isPRCancelled || isAnyApprovalRejected}
                          customStyles={{ minWidth: 100, height: 36 }}
                        />
                      </Box>
                    )}

                  {status.label === "Swageazy" &&
                    userStore.hasPermission(
                      EmployeeRoles.SWAGEAZY_INVENTORY_PR_ACTIONS,
                    ) &&
                    Boolean(prDetail.third_approval_required) &&
                    !prDetail.swageazy_approval &&
                    (prDetail.rejected_by_swageazy_admin_id === null ||
                      prDetail.rejected_by_swageazy_admin_id <= 0) && (
                      <Box sx={{ mt: 1.5, display: "flex", gap: 1 }}>
                        <AppButton
                          label="Approve"
                          variant="primary"
                          onClick={() => setSwageazyApprovePopupOpen(true)}
                          disabled={isPRCancelled || isAnyApprovalRejected}
                          customStyles={{ minWidth: 100, height: 36 }}
                        />
                        <AppButton
                          label="Reject"
                          variant="secondary"
                          onClick={() => setSwageazyRejectPopupOpen(true)}
                          disabled={isPRCancelled || isAnyApprovalRejected}
                          customStyles={{ minWidth: 100, height: 36 }}
                        />
                      </Box>
                    )}
                </Box>
              ))}
            </Box>
          </Box>
          {purchaseRequestStore.purchaseRequestDetail
            .rejected_by_finance_admin_id ||
            purchaseRequestStore.purchaseRequestDetail
              .rejected_by_sourcing_admin_id ||
            purchaseRequestStore.purchaseRequestDetail
              .rejected_by_swageazy_admin_id ? (
            <Box
              sx={{
                backgroundColor: theme.palette.error.main + "15",
                border: `1px solid ${theme.palette.error.main}40`,
                borderRadius: 2,
                padding: 2,
                marginBottom: 2,
                borderLeft: `4px solid ${theme.palette.error.main}`,
              }}
            >
              <CustomTypography
                variant="body1"
                color="error.main"
                fontWeight={600}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                Purchase Request Rejected
              </CustomTypography>
              <CustomTypography
                variant="body2"
                color="error.main"
                sx={{ marginTop: 1 }}
              >
                <strong>Rejected by:</strong>{" "}
                {purchaseRequestStore.purchaseRequestDetail
                  .rejected_by_finance_adminname ||
                  purchaseRequestStore.purchaseRequestDetail
                    .rejected_by_sourcing_adminname ||
                  purchaseRequestStore.purchaseRequestDetail
                    .rejected_by_swageazy_adminname}{" "}
                <br />
                <strong>Rejected date:</strong>{" "}
                {formatDate(
                  purchaseRequestStore.purchaseRequestDetail
                    .rejection_date_by_finance ||
                  purchaseRequestStore.purchaseRequestDetail
                    .rejection_date_by_sourcing ||
                  purchaseRequestStore.purchaseRequestDetail
                    .rejection_date_by_swageazy,
                )}
              </CustomTypography>
            </Box>
          ) : null}

          {/* Details Grid */}
          <Grid2 container spacing={3}>
            {/* Vendor Information */}
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Card
                sx={{
                  height: "100%",
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.06)",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    boxShadow: "0px 4px 16px 0px rgba(0, 0, 0, 0.12)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mb: 3,
                      pb: 2,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        backgroundColor: theme.palette.primary.main + "15",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <BusinessIcon
                        sx={{ color: "primary.main", fontSize: 20 }}
                      />
                    </Box>
                    <CustomTypography variant="heading6" fontWeight={600}>
                      Vendor Information
                    </CustomTypography>
                  </Box>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <Box>
                      <CustomTypography
                        variant="body2"
                        color="text.secondary"
                        fontWeight={500}
                        sx={{ mb: 0.5 }}
                      >
                        Vendor Name
                      </CustomTypography>
                      <CustomTypography
                        variant="body1"
                        fontWeight={600}
                        color="text.primary"
                      >
                        {purchaseRequestStore.purchaseRequestDetail.vendor_name}
                      </CustomTypography>
                    </Box>
                    <Box>
                      <CustomTypography
                        variant="body2"
                        color="text.secondary"
                        fontWeight={500}
                        sx={{ mb: 0.5 }}
                      >
                        Email Address
                      </CustomTypography>
                      <CustomTypography
                        variant="body1"
                        fontWeight={600}
                        color="text.primary"
                      >
                        {
                          purchaseRequestStore.purchaseRequestDetail
                            .vendor_email
                        }
                      </CustomTypography>
                    </Box>
                    <Box>
                      {purchaseRequestStore.purchaseRequestDetail
                        .bill_from_gst && (
                          <>
                            <CustomTypography
                              variant="body2"
                              color="text.secondary"
                              fontWeight={500}
                              sx={{ mb: 0.5 }}
                            >
                              Bill From
                            </CustomTypography>
                            <CustomTypography
                              variant="body1"
                              fontWeight={600}
                              color="text.primary"
                            >
                              {
                                purchaseRequestStore.purchaseRequestDetail
                                  .bill_from_gst
                              }{" "}
                              (
                              {
                                purchaseRequestStore.purchaseRequestDetail
                                  .bill_from_state
                              }
                              )
                            </CustomTypography>
                          </>
                        )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid2>

            {/* Payment Information */}
            {/* <Grid2 size={{ xs: 12, md: 4 }}>
              <Card
                sx={{
                  height: "100%",
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.06)",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    boxShadow: "0px 4px 16px 0px rgba(0, 0, 0, 0.12)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mb: 3,
                      pb: 2,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        backgroundColor: theme.palette.success.main + "15",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CheckCircleIcon
                        sx={{ color: "success.main", fontSize: 20 }}
                      />
                    </Box>
                    <CustomTypography variant="heading6" fontWeight={600}>
                      Payment Details
                    </CustomTypography>
                  </Box>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <Box>
                      <CustomTypography
                        variant="body2"
                        color="text.secondary"
                        fontWeight={500}
                        sx={{ mb: 0.5 }}
                      >
                        Expected Payment Date
                      </CustomTypography>
                      <CustomTypography
                        variant="body1"
                        fontWeight={600}
                        color="text.primary"
                      >
                        {purchaseRequestStore.purchaseRequestDetail
                          .expected_payment_date
                          ? formatDate(
                              purchaseRequestStore.purchaseRequestDetail
                                .expected_payment_date
                            )
                          : "Not specified"}
                      </CustomTypography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid2> */}

            {/* Approval Information */}
            <Grid2 size={{ xs: 12, md: 6 }}>
              <Card
                sx={{
                  height: "100%",
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.06)",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    boxShadow: "0px 4px 16px 0px rgba(0, 0, 0, 0.12)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mb: 3,
                      pb: 2,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        backgroundColor: theme.palette.warning.main + "15",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <AccountBalanceIcon
                        sx={{ color: "warning.main", fontSize: 20 }}
                      />
                    </Box>
                    <CustomTypography variant="heading6" fontWeight={600}>
                      Approval Details
                    </CustomTypography>
                  </Box>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    {purchaseRequestStore.purchaseRequestDetail
                      .finance_approval === 1 &&
                      purchaseRequestStore.purchaseRequestDetail
                        .approved_by_finance_adminname && (
                        <Box>
                          <CustomTypography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={500}
                            sx={{ mb: 0.5 }}
                          >
                            Finance Approved By
                          </CustomTypography>
                          <CustomTypography
                            variant="body1"
                            fontWeight={600}
                            color="success.main"
                          >
                            {
                              purchaseRequestStore.purchaseRequestDetail
                                .approved_by_finance_adminname
                            }
                          </CustomTypography>
                          <CustomTypography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={500}
                            sx={{ mt: 0.5 }}
                          >
                            Approved Date:{" "}
                            {formatDate(
                              purchaseRequestStore.purchaseRequestDetail
                                .finance_approval_date,
                            )}
                          </CustomTypography>
                        </Box>
                      )}

                    {purchaseRequestStore.purchaseRequestDetail
                      .rejected_by_finance_admin_id != null &&
                      purchaseRequestStore.purchaseRequestDetail
                        .rejected_by_finance_admin_id > 0 && (
                        <Box>
                          <CustomTypography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={500}
                            sx={{ mb: 0.5 }}
                          >
                            Finance Rejected By
                          </CustomTypography>
                          <CustomTypography
                            variant="body1"
                            fontWeight={600}
                            color="error.main"
                          >
                            {purchaseRequestStore.purchaseRequestDetail
                              .rejected_by_finance_adminname || "-"}
                          </CustomTypography>
                          <CustomTypography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={500}
                            sx={{ mt: 0.5 }}
                          >
                            Rejected Date:{" "}
                            {formatDate(
                              purchaseRequestStore.purchaseRequestDetail
                                .rejection_date_by_finance,
                            )}
                          </CustomTypography>
                          {purchaseRequestStore.purchaseRequestDetail
                            .rejection_reason_by_finance && (
                              <CustomTypography
                                variant="body2"
                                color="error.main"
                                fontWeight={500}
                                sx={{ mt: 0.5 }}
                              >
                                Rejection Reason:{" "}
                                {
                                  purchaseRequestStore.purchaseRequestDetail
                                    .rejection_reason_by_finance
                                }
                              </CustomTypography>
                            )}
                        </Box>
                      )}
                    {purchaseRequestStore.purchaseRequestDetail
                      .sourcing_approval === 1 &&
                      purchaseRequestStore.purchaseRequestDetail
                        .approved_by_sourcing_adminname && (
                        <Box>
                          <CustomTypography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={500}
                            sx={{ mb: 0.5 }}
                          >
                            Sourcing Approved By
                          </CustomTypography>
                          <CustomTypography
                            variant="body1"
                            fontWeight={600}
                            color="success.main"
                          >
                            {
                              purchaseRequestStore.purchaseRequestDetail
                                .approved_by_sourcing_adminname
                            }
                          </CustomTypography>
                          <CustomTypography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={500}
                            sx={{ mt: 0.5 }}
                          >
                            Approved Date:{" "}
                            {formatDate(
                              purchaseRequestStore.purchaseRequestDetail
                                .sourcing_approval_date,
                            )}
                          </CustomTypography>
                        </Box>
                      )}

                    {purchaseRequestStore.purchaseRequestDetail
                      .rejected_by_sourcing_admin_id != null &&
                      purchaseRequestStore.purchaseRequestDetail
                        .rejected_by_sourcing_admin_id > 0 && (
                        <Box>
                          <CustomTypography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={500}
                            sx={{ mb: 0.5 }}
                          >
                            Sourcing Rejected By
                          </CustomTypography>
                          <CustomTypography
                            variant="body1"
                            fontWeight={600}
                            color="error.main"
                          >
                            {purchaseRequestStore.purchaseRequestDetail
                              .rejected_by_sourcing_adminname || "-"}
                          </CustomTypography>
                          <CustomTypography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={500}
                            sx={{ mt: 0.5 }}
                          >
                            Rejected Date:{" "}
                            {formatDate(
                              purchaseRequestStore.purchaseRequestDetail
                                .rejection_date_by_sourcing,
                            )}
                          </CustomTypography>
                          {purchaseRequestStore.purchaseRequestDetail
                            .rejection_reason_by_sourcing && (
                              <CustomTypography
                                variant="body2"
                                color="error.main"
                                fontWeight={500}
                                sx={{ mt: 0.5 }}
                              >
                                Rejection Reason:{" "}
                                {
                                  purchaseRequestStore.purchaseRequestDetail
                                    .rejection_reason_by_sourcing
                                }
                              </CustomTypography>
                            )}
                        </Box>
                      )}

                    {purchaseRequestStore.purchaseRequestDetail
                      .swageazy_approval === 1 && (
                        <Box>
                          <CustomTypography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={500}
                            sx={{ mb: 0.5 }}
                          >
                            Swageazy Approved By
                          </CustomTypography>
                          <CustomTypography
                            variant="body1"
                            fontWeight={600}
                            color="success.main"
                          >
                            {purchaseRequestStore.purchaseRequestDetail
                              .approved_by_swageazy_adminname ?? "-"}
                          </CustomTypography>
                          <CustomTypography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={500}
                            sx={{ mt: 0.5 }}
                          >
                            Approved Date:{" "}
                            {formatDate(
                              purchaseRequestStore.purchaseRequestDetail
                                .swageazy_approval_date,
                            )}
                          </CustomTypography>
                        </Box>
                      )}

                    {purchaseRequestStore.purchaseRequestDetail
                      .rejected_by_swageazy_admin_id != null &&
                      purchaseRequestStore.purchaseRequestDetail
                        .rejected_by_swageazy_admin_id > 0 && (
                        <Box>
                          <CustomTypography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={500}
                            sx={{ mb: 0.5 }}
                          >
                            Swageazy Rejected By
                          </CustomTypography>
                          <CustomTypography
                            variant="body1"
                            fontWeight={600}
                            color="error.main"
                          >
                            {purchaseRequestStore.purchaseRequestDetail
                              .rejected_by_swageazy_adminname || "-"}
                          </CustomTypography>
                          <CustomTypography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={500}
                            sx={{ mt: 0.5 }}
                          >
                            Rejected Date:{" "}
                            {formatDate(
                              purchaseRequestStore.purchaseRequestDetail
                                .rejection_date_by_swageazy,
                            )}
                          </CustomTypography>
                          {purchaseRequestStore.purchaseRequestDetail
                            .rejection_reason_by_swageazy && (
                              <CustomTypography
                                variant="body2"
                                color="error.main"
                                fontWeight={500}
                                sx={{ mt: 0.5 }}
                              >
                                Rejection Reason:{" "}
                                {
                                  purchaseRequestStore.purchaseRequestDetail
                                    .rejection_reason_by_swageazy
                                }
                              </CustomTypography>
                            )}
                        </Box>
                      )}
                    {purchaseRequestStore.purchaseRequestDetail
                      .finance_approval !== 1 &&
                      (purchaseRequestStore.purchaseRequestDetail
                        .rejected_by_finance_admin_id === null ||
                        purchaseRequestStore.purchaseRequestDetail
                          .rejected_by_finance_admin_id <= 0) &&
                      purchaseRequestStore.purchaseRequestDetail
                        .sourcing_approval !== 1 &&
                      (purchaseRequestStore.purchaseRequestDetail
                        .rejected_by_sourcing_admin_id === null ||
                        purchaseRequestStore.purchaseRequestDetail
                          .rejected_by_sourcing_admin_id <= 0) && (
                        <Box>
                          <CustomTypography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={500}
                            sx={{ mb: 0.5 }}
                          >
                            Status
                          </CustomTypography>
                          <CustomTypography
                            variant="body1"
                            fontWeight={600}
                            color="warning.main"
                          >
                            Pending Approvals
                          </CustomTypography>
                        </Box>
                      )}

                    {purchaseRequestStore.purchaseRequestDetail
                      .pr_cancellation_date ? (
                      <Box>
                        <CustomTypography
                          variant="body2"
                          color="error.main"
                          fontWeight={600}
                          sx={{ mt: 1 }}
                        >
                          Cancelled
                        </CustomTypography>

                        <CustomTypography
                          variant="body2"
                          color="text.secondary"
                          fontWeight={500}
                          sx={{ mt: 0.5 }}
                        >
                          Cancellation Date:{" "}
                          {formatDate(
                            purchaseRequestStore.purchaseRequestDetail
                              .pr_cancellation_date,
                          )}
                        </CustomTypography>

                        <CustomTypography
                          variant="body2"
                          color="text.secondary"
                          fontWeight={500}
                          sx={{ mt: 0.5 }}
                        >
                          Cancelled By:{" "}
                          {
                            purchaseRequestStore.purchaseRequestDetail
                              .pr_cancelled_by_admin_name
                          }
                        </CustomTypography>

                        <CustomTypography
                          variant="body2"
                          color="error.main"
                          fontWeight={500}
                          sx={{ mt: 0.5 }}
                        >
                          Cancellation Reason:{" "}
                          {purchaseRequestStore.purchaseRequestDetail
                            .pr_cancellation_reason || "-"}
                        </CustomTypography>
                      </Box>
                    ) : (
                      <Box sx={{ mt: 1.5, display: "flex", gap: 1 }}>
                        {
                          <Box sx={{ flex: 1 }}>
                            <AppButton
                              label="Cancel PR"
                              variant="secondary"
                              onClick={handleCancelPRClick}
                              disabled={
                                (purchaseRequestStore.purchaseRequestDetail
                                  .purchase_order_invoices?.length ?? 0) > 0 ||
                                purchaseRequestStore.purchaseRequestDetail
                                  .active_po === 1 ||
                                (
                                  purchaseRequestStore.purchaseRequestDetail
                                    .purchase_orders ?? []
                                ).some(
                                  (po) => !isPoCancelledOrClosed(po.status),
                                )
                              }
                              customStyles={{
                                width: "100%",
                                borderRadius: 2,
                              }}
                            />
                          </Box>
                        }

                        <Box sx={{ flex: 1 }}>
                          <AppButton
                            label={
                              prDetail.po_id > 0 ? "Regenerate" : "Generate PO"
                            }
                            variant="primary"
                            onClick={handleGeneratePO}
                            disabled={
                              !prDetail.sourcing_approval ||
                              !prDetail.finance_approval ||
                              prDetail.active === 0
                            }
                            customStyles={{
                              width: "100%",
                              borderRadius: 2,
                            }}
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid2>
          </Grid2>
        </Paper>
      </Box>

      {/* Section Divider */}
      <Box
        sx={{
          height: 2,
          background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.success.main} 50%, ${theme.palette.warning.main} 100%)`,
          borderRadius: 1,
          mb: 4,
        }}
      />

      {/* Section 2: Products Details */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 3,
            p: 2,
            backgroundColor: theme.palette.success.main + "08",
            borderRadius: 2,
            border: `2px solid ${theme.palette.success.main}20`,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              backgroundColor: theme.palette.success.main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ShoppingCartIcon sx={{ color: "white", fontSize: 20 }} />
          </Box>
          <Box>
            <CustomTypography
              variant="heading5"
              fontWeight={700}
              color="success.main"
            >
              Products Details
            </CustomTypography>
            <CustomTypography variant="body2" color="text.secondary">
              {purchaseRequestStore.purchaseRequestDetail.products?.length || 0}{" "}
              items in this purchase request
            </CustomTypography>
          </Box>
        </Box>

        {showProductEditIcon && hasPostPoEditPermission  && (
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            Any changes made to the quantity or HSN code will not impact the associated uploaded vendor invoices.
          </Alert>
        )}
      </Box>

      <Box sx={{ mb: 4 }}>
        {purchaseRequestStore.purchaseRequestDetail.products?.map(
          (product, index) => {
            const productInsights =
              projectSourcingOpsStore.purchaseRequestProductInsights?.[
              product.product_id
              ] || [];

            return (
              <Card
                key={product.product_id}
                sx={{
                  mb: 3,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 3,
                  boxShadow: "0px 4px 20px 0px rgba(0, 0, 0, 0.08)",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    boxShadow: "0px 8px 32px 0px rgba(0, 0, 0, 0.12)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  {/* Product Header */}
                  <Box sx={{ mb: 4 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 3,
                        pb: 3,
                        borderBottom: `2px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <CustomTypography
                            variant="heading5"
                            fontWeight={700}
                            color="primary.main"
                          >
                            {index + 1}.
                          </CustomTypography>
                          <CustomTypography
                            variant="heading5"
                            fontWeight={700}
                            color="primary.main"
                          >
                            {product.product_name}
                          </CustomTypography>
                          <Chip
                            label={`ID: ${product.product_id}`}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                          {/* <Chip
                            label={`SKU: ${product.sku || "N/A"}`}
                            size="small"
                            variant="outlined"
                            color="secondary"
                          /> */}
                        </Box>

                        {Boolean(product.is_voucher) && (
                          <Box sx={{ display: "flex", gap: 2 }}>
                            {product.voucher_count > 0 && (
                              <AppButton
                                variant="transparentPrimary"
                                label="View Vouchers"
                                onClick={() => {
                                  window.open(
                                    `/vouchers?page_no=1&limit=30&sort_by=id&sort_type=DESC&order_id=${purchaseRequestStore
                                      .purchaseRequestDetail!.order_id
                                    }`,
                                    "_blank",
                                  );
                                }}
                                disabled={
                                  !(
                                    purchaseRequestStore.purchaseRequestDetail
                                      ?.finance_approval === 1 &&
                                    purchaseRequestStore.purchaseRequestDetail
                                      ?.sourcing_approval === 1 &&
                                    (purchaseRequestStore.purchaseRequestDetail
                                      ?.rejected_by_swageazy_admin_id ===
                                      null ||
                                      purchaseRequestStore.purchaseRequestDetail
                                        ?.rejected_by_swageazy_admin_id ===
                                      undefined)
                                  )
                                }
                                customStyles={{ minWidth: 140, height: 40 }}
                              />
                            )}
                            {product.voucher_count < product.quantity && (
                              <AppButton
                                variant="primary"
                                label="Purchase"
                                onClick={() => {
                                  setSelectedProductForVouchers(product);
                                  setPurchaseVouchersPopupOpen(true);
                                }}
                                disabled={
                                  !(
                                    purchaseRequestStore.purchaseRequestDetail
                                      ?.finance_approval === 1 &&
                                    purchaseRequestStore.purchaseRequestDetail
                                      ?.sourcing_approval === 1 &&
                                    (purchaseRequestStore.purchaseRequestDetail
                                      ?.rejected_by_swageazy_admin_id ===
                                      null ||
                                      purchaseRequestStore.purchaseRequestDetail
                                        ?.rejected_by_swageazy_admin_id ===
                                      undefined)
                                  )
                                }
                                customStyles={{ minWidth: 140, height: 40 }}
                              />
                            )}
                          </Box>
                        )}
                      </Box>
                    </Box>

                    {/* Product Details Grid */}
                    <Grid2 container spacing={3}>
                      <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                        <Box
                          sx={{
                            p: 2.5,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 2,
                            backgroundColor: theme.palette.background.paper,
                            transition: "all 0.2s ease-in-out",
                            minHeight: "80px",
                            display: "flex",
                            flexDirection: "column",
                            "&:hover": {
                              boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.1)",
                              borderColor: theme.palette.primary.main + "40",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 1,
                              minHeight: "24px",
                            }}
                          >
                            <CustomTypography
                              variant="body2"
                              color="text.secondary"
                              fontWeight={500}
                            >
                              HSN Code
                            </CustomTypography>
                            {showProductEditIcon && (
                              <IconButton
                                sx={{
                                  width: "10px",
                                  "&:hover": {
                                    backgroundColor: "transparent",
                                  },
                                  "&:active": {
                                    backgroundColor: "transparent",
                                  },
                                }}
                                onClick={() =>
                                  handleEditProduct(product, "hsn_code")
                                }
                              >
                                <Icon sx={{ fontSize: "12px" }}>
                                  <img src={pencil_uderline} alt="edit-icon" />
                                </Icon>
                              </IconButton>
                            )}
                          </Box>
                          <CustomTypography
                            variant="body1"
                            fontWeight={600}
                            color="text.primary"
                          >
                            {product.hsn_code || "N/A"}
                          </CustomTypography>
                        </Box>
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                        <Box
                          sx={{
                            p: 2.5,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 2,
                            backgroundColor: theme.palette.background.paper,
                            transition: "all 0.2s ease-in-out",
                            minHeight: "80px",
                            display: "flex",
                            flexDirection: "column",
                            "&:hover": {
                              boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.1)",
                              borderColor: theme.palette.info.main + "40",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 1,
                              minHeight: "24px",
                            }}
                          >
                            <CustomTypography
                              variant="body2"
                              color="text.secondary"
                              fontWeight={500}
                            >
                              Quantity
                            </CustomTypography>
                            {showProductEditIcon && (
                              <IconButton
                                sx={{
                                  width: "10px",
                                  "&:hover": {
                                    backgroundColor: "transparent",
                                  },
                                  "&:active": {
                                    backgroundColor: "transparent",
                                  },
                                }}
                                onClick={() =>
                                  handleEditProduct(product, "quantity")
                                }
                              >
                                <Icon sx={{ fontSize: "12px" }}>
                                  <img src={pencil_uderline} alt="edit-icon" />
                                </Icon>
                              </IconButton>
                            )}
                          </Box>
                          <CustomTypography
                            variant="body1"
                            fontWeight={600}
                            color="text.primary"
                          >
                            {product.quantity} units
                          </CustomTypography>
                        </Box>
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                        <Box
                          sx={{
                            p: 2.5,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 2,
                            backgroundColor: theme.palette.background.paper,
                            transition: "all 0.2s ease-in-out",
                            minHeight: "80px",
                            display: "flex",
                            flexDirection: "column",
                            "&:hover": {
                              boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.1)",
                              borderColor: theme.palette.success.main + "40",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 1,
                              minHeight: "24px",
                            }}
                          >
                            <CustomTypography
                              variant="body2"
                              color="text.secondary"
                              fontWeight={500}
                            >
                              Rate
                            </CustomTypography>
                            {showProductEditIcon && (
                              <IconButton
                                sx={{
                                  width: "10px",
                                  "&:hover": {
                                    backgroundColor: "transparent",
                                  },
                                  "&:active": {
                                    backgroundColor: "transparent",
                                  },
                                }}
                                onClick={() =>
                                  handleEditProduct(product, "rate")
                                }
                              >
                                <Icon sx={{ fontSize: "12px" }}>
                                  <img src={pencil_uderline} alt="edit-icon" />
                                </Icon>
                              </IconButton>
                            )}
                          </Box>
                          <CustomTypography
                            variant="body1"
                            fontWeight={600}
                            color="text.primary"
                          >
                            ₹{product.rate}
                          </CustomTypography>
                        </Box>
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                        <Box
                          sx={{
                            p: 2.5,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 2,
                            backgroundColor: theme.palette.background.paper,
                            transition: "all 0.2s ease-in-out",
                            minHeight: "80px",
                            display: "flex",
                            flexDirection: "column",
                            "&:hover": {
                              boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.1)",
                              borderColor: theme.palette.warning.main + "40",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 1,
                              minHeight: "24px",
                            }}
                          >
                            <CustomTypography
                              variant="body2"
                              color="text.secondary"
                              fontWeight={500}
                            >
                              Selling Price
                            </CustomTypography>
                            {/* <IconButton
                              sx={{
                                width: "10px",
                                "&:hover": {
                                  backgroundColor: "transparent",
                                },
                                "&:active": {
                                  backgroundColor: "transparent",
                                },
                              }}
                              onClick={() =>
                                handleEditProduct(product, "selling_price")
                              }
                            >
                              <Icon sx={{ fontSize: "12px" }}>
                                <img src={pencil_uderline} alt="edit-icon" />
                              </Icon>
                            </IconButton> */}
                          </Box>
                          <CustomTypography
                            variant="body1"
                            fontWeight={600}
                            color="text.primary"
                          >
                            ₹{product.selling_price}
                          </CustomTypography>
                        </Box>
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                        <Box
                          sx={{
                            p: 2.5,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 2,
                            backgroundColor: theme.palette.background.paper,
                            transition: "all 0.2s ease-in-out",
                            minHeight: "80px",
                            display: "flex",
                            flexDirection: "column",
                            "&:hover": {
                              boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.1)",
                              borderColor: theme.palette.error.main + "40",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 1,
                              minHeight: "24px",
                            }}
                          >
                            <CustomTypography
                              variant="body2"
                              color="text.secondary"
                              fontWeight={500}
                            >
                              GST %
                            </CustomTypography>
                            {showProductEditIcon &&
                              (showReactivatePoAction ||
                                !(
                                  purchaseRequestStore.purchaseRequestDetail
                                    ?.swageazy_approval === 1 ||
                                  purchaseRequestStore.purchaseRequestDetail
                                    ?.sourcing_approval === 1 ||
                                  purchaseRequestStore.purchaseRequestDetail
                                    ?.finance_approval === 1
                                )) && (
                                <IconButton
                                  sx={{
                                    width: "10px",
                                    "&:hover": {
                                      backgroundColor: "transparent",
                                    },
                                    "&:active": {
                                      backgroundColor: "transparent",
                                    },
                                  }}
                                  onClick={() =>
                                    handleEditProduct(product, "gst_percentage")
                                  }
                                >
                                  <Icon sx={{ fontSize: "12px" }}>
                                    <img
                                      src={pencil_uderline}
                                      alt="edit-icon"
                                    />
                                  </Icon>
                                </IconButton>
                              )}
                          </Box>
                          <CustomTypography
                            variant="body1"
                            fontWeight={600}
                            color="text.primary"
                          >
                            {product.gst_percentage || "N/A"}
                          </CustomTypography>
                        </Box>
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                        <Box
                          sx={{
                            p: 2.5,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 2,
                            backgroundColor: theme.palette.background.paper,
                            transition: "all 0.2s ease-in-out",
                            minHeight: "80px",
                            display: "flex",
                            flexDirection: "column",
                            "&:hover": {
                              boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.1)",
                              borderColor: theme.palette.secondary.main + "40",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              minHeight: "24px",
                              mb: 1,
                            }}
                          >
                            <CustomTypography
                              variant="body2"
                              color="text.secondary"
                              fontWeight={500}
                            >
                              Balance Qty
                            </CustomTypography>
                          </Box>
                          <CustomTypography
                            variant="body1"
                            fontWeight={600}
                            color="text.primary"
                          >
                            {purchaseRequestStore.prProductAvailableQty[
                              product.product_id
                            ] || "N/A"}
                          </CustomTypography>
                        </Box>
                      </Grid2>
                    </Grid2>

                    {/* Product Description */}
                    {product.description && (
                      <Box
                        sx={{
                          mt: 4,
                          p: 3,
                          backgroundColor: theme.palette.grey[50],
                          borderRadius: 2,
                          border: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <CustomTypography
                          variant="body2"
                          color="text.secondary"
                          fontWeight={600}
                          sx={{ mb: 1.5 }}
                        >
                          Product Description
                        </CustomTypography>
                        <CustomTypography variant="body1" color="text.primary">
                          {product.description}
                        </CustomTypography>
                      </Box>
                    )}
                  </Box>

                  {/* PO History Section - Accordion */}
                  <Box
                    sx={{
                      borderTop: `2px solid ${theme.palette.divider}`,
                      pt: 4,
                      mt: 4,
                    }}
                  >
                    <Accordion
                      sx={{
                        boxShadow: "none",
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        "&:before": {
                          display: "none",
                        },
                        "&.Mui-expanded": {
                          margin: 0,
                        },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          backgroundColor: theme.palette.grey[50],
                          borderRadius: "8px 8px 0 0",
                          "&.Mui-expanded": {
                            minHeight: "auto",
                            borderRadius: "8px 8px 0 0",
                          },
                          "& .MuiAccordionSummary-content": {
                            margin: "12px 0",
                            "&.Mui-expanded": {
                              margin: "12px 0",
                            },
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: "50%",
                              backgroundColor:
                                theme.palette.secondary.main + "15",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <DownloadIcon
                              sx={{ color: "secondary.main", fontSize: 20 }}
                            />
                          </Box>
                          <CustomTypography variant="heading6" fontWeight={600}>
                            Purchase Order History
                          </CustomTypography>
                          {productInsights && productInsights.length > 0 && (
                            <Chip
                              label={`${productInsights.length} items`}
                              size="small"
                              color="secondary"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails
                        sx={{
                          p: 3,
                          backgroundColor: theme.palette.background.paper,
                        }}
                      >
                        {!productInsights || productInsights.length === 0 ? (
                          <Box
                            sx={{
                              textAlign: "center",
                              py: 6,
                              px: 4,
                              backgroundColor: theme.palette.grey[50],
                              borderRadius: 2,
                              border: `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            <ShoppingCartIcon
                              sx={{
                                fontSize: 48,
                                color: "text.secondary",
                                mb: 2,
                              }}
                            />
                            <CustomTypography
                              variant="body1"
                              color="text.secondary"
                              fontWeight={500}
                            >
                              No purchase order history available for this
                              product
                            </CustomTypography>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 3,
                            }}
                          >
                            {productInsights.map(
                              (
                                insight: PurchaseRequestProductInsights,
                                index: number,
                              ) => (
                                <Box
                                  key={index}
                                  sx={{
                                    p: 3,
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: 2,
                                    backgroundColor:
                                      theme.palette.background.paper,
                                    boxShadow:
                                      "0px 2px 8px 0px rgba(0, 0, 0, 0.06)",
                                    transition: "all 0.2s ease-in-out",
                                    "&:hover": {
                                      boxShadow:
                                        "0px 4px 16px 0px rgba(0, 0, 0, 0.12)",
                                      transform: "translateY(-2px)",
                                      borderColor:
                                        theme.palette.primary.main + "40",
                                    },
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: {
                                        lg: "flex",
                                        xl: "flex",
                                        md: "flex",
                                        sm: "block",
                                        xs: "block",
                                      },
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Box>
                                      <CustomTypography
                                        variant="body1"
                                        fontWeight={600}
                                      >
                                        {`${insight.title} - PR ${insight.pr_id
                                          } was raised for ${insight.customer_name
                                          } @ ${insight.rate} by ${insight.created_by_admin_name
                                          } on this ${formatDate(
                                            insight.created_date,
                                          )}.`}
                                      </CustomTypography>
                                    </Box>
                                    <Box
                                      mt={{
                                        xs: 2,
                                        sm: 2,
                                        md: 0,
                                        lg: 0,
                                        xl: 0,
                                      }}
                                    >
                                      <AppButton
                                        label={`Download PO-${insight.po_id}`}
                                        variant="primary"
                                        startIcon={<DownloadIcon />}
                                        onClick={() =>
                                          handleDownloadPO(insight.po_id)
                                        }
                                      />
                                    </Box>
                                  </Box>
                                </Box>
                              ),
                            )}
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                </CardContent>
              </Card>
            );
          },
        )}
      </Box>

      {/* Section 3: Purchase Orders */}
      {purchaseRequestStore.purchaseRequestDetail.purchase_orders &&
        purchaseRequestStore.purchaseRequestDetail.purchase_orders.length >
        0 && (
          <>
            <Box
              sx={{
                height: 2,
                background: `linear-gradient(90deg, ${theme.palette.success.main} 0%, ${theme.palette.warning.main} 50%, ${theme.palette.info.main} 100%)`,
                borderRadius: 1,
                mb: 4,
              }}
            />
            <Box sx={{ mb: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 3,
                  p: 2,
                  backgroundColor: theme.palette.warning.main + "08",
                  borderRadius: 2,
                  border: `2px solid ${theme.palette.warning.main}20`,
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    backgroundColor: theme.palette.warning.main,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <DownloadIcon sx={{ color: "white", fontSize: 20 }} />
                </Box>
                <Box>
                  <CustomTypography
                    variant="heading5"
                    fontWeight={700}
                    color="warning.main"
                  >
                    Purchase Orders
                  </CustomTypography>
                  <CustomTypography variant="body2" color="text.secondary">
                    {
                      purchaseRequestStore.purchaseRequestDetail.purchase_orders
                        .length
                    }{" "}
                    PO created
                  </CustomTypography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {purchaseRequestStore.purchaseRequestDetail.purchase_orders.map(
                  (order, index) => (
                    <Card
                      key={order.id}
                      sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.06)",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          boxShadow: "0px 4px 16px 0px rgba(0, 0, 0, 0.12)",
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            mb: { xs: 3, sm: 2 },
                            gap: 2,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                backgroundColor: theme.palette.secondary.main,
                              }}
                            />
                            <CustomTypography
                              variant="heading6"
                              fontWeight={600}
                              color="text.primary"
                            >
                              PO #{order.id}
                            </CustomTypography>
                            <Chip
                              label={order.status}
                              size="small"
                              color={
                                order.status === "CREATED"
                                  ? "info"
                                  : order.status === "OPEN"
                                    ? "info"
                                    : order.status === "SENT"
                                      ? "success"
                                      : order.status === "CANCELLED"
                                        ? "error"
                                        : "default"
                              }
                              variant="outlined"
                            />
                            {order.status !== "CANCELLED" &&
                              order.status !== "CLOSED" && (
                                <AppDropDown
                                  title="Update Status"
                                  isMultiSelect={false}
                                  list={PurchaseOrderStatusOptions}
                                  value={order.status}
                                  onSingleSelectionChange={(status) =>
                                    handleStatusChange(order.id, status)
                                  }
                                  disabled={purchaseRequestStore.updatingStatus.has(
                                    order.id,
                                  )}
                                  width="120px"
                                  height="32px"
                                  fontSize="0.8rem"
                                />
                              )}
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",

                              gap: { xs: 1, sm: 2 },
                              flexWrap: "wrap",
                            }}
                          >
                            {showReactivatePoAction &&
                              isPoCancelledOrClosed(order.status) && (
                                <AppButton
                                  label="Reactivate PO"
                                  variant="primary"
                                  onClick={() =>
                                    handleOpenReactivatePo(order.id)
                                  }
                                  customStyles={{
                                    minWidth: "auto",
                                    px: 2,
                                    py: 0.5,
                                    height: "32px",
                                  }}
                                />
                              )}
                            {!order.cancelled_by_admin_id &&
                              order.status !== "CANCELLED" &&
                              order.status !== "CLOSED" && (
                                <>
                                  <IconButton
                                    onClick={() => handleDownloadPO(order.id)}
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      backgroundColor:
                                        theme.palette.primary.main + "15",
                                      "&:hover": {
                                        backgroundColor:
                                          theme.palette.primary.main + "25",
                                        transform: "scale(1.05)",
                                      },
                                      flexShrink: 0,
                                    }}
                                  >
                                    <DownloadIcon
                                      sx={{
                                        color: "primary.main",
                                        fontSize: 16,
                                      }}
                                    />
                                  </IconButton>
                                  {userStore.hasPermission(
                                    EmployeeRoles.UPLOAD_PURCHASE_ORDER_INVOICE,
                                  ) && (
                                      <AppButton
                                        label="Upload Invoice"
                                        onClick={() =>
                                          handleOpenInvoiceUpload(order.id)
                                        }
                                        variant="primary"
                                        customStyles={{
                                          minWidth: "auto",
                                          px: 2,
                                          py: 0.5,
                                        }}
                                      />
                                    )}
                                  <AppButton
                                    label={
                                      purchaseRequestStore.sendingEmails.has(
                                        order.id,
                                      ) ? (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                          }}
                                        >
                                          <CircularProgress
                                            size={14}
                                            sx={{ color: "success.main" }}
                                          />
                                          {order.email_sent
                                            ? "Resending..."
                                            : "Sending..."}
                                        </Box>
                                      ) : order.email_sent ? (
                                        "Resend"
                                      ) : (
                                        "Send Email"
                                      )
                                    }
                                    onClick={() => handleSendEmail(order.id)}
                                    disabled={purchaseRequestStore.sendingEmails.has(
                                      order.id,
                                    )}
                                    variant="secondary"
                                    customStyles={{
                                      minWidth: "auto",
                                      px: 2,
                                      py: 0.5,
                                      height: "32px",
                                      fontSize: "0.75rem",
                                      borderColor: theme.palette.success.main,
                                      whiteSpace: "nowrap",
                                      "@media (max-width: 600px)": {
                                        fontSize: "0.7rem",
                                        px: 1.5,
                                        minWidth: "100px",
                                      },
                                      "&:disabled": {
                                        borderColor: theme.palette.grey[400],
                                        color: theme.palette.grey[500],
                                        backgroundColor: "transparent",
                                      },
                                    }}
                                  />
                                </>
                              )}
                          </Box>
                        </Box>

                        <Grid2 container spacing={2}>
                          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                            <Box>
                              <CustomTypography
                                variant="body1"
                                color="text.secondary"
                                fontWeight={500}
                                sx={{ mb: 0.5 }}
                              >
                                Created By - {order.created_by_adminname}
                              </CustomTypography>
                            </Box>
                          </Grid2>
                          <Grid2 size={{ xs: 12, sm: 6, md: 3.5 }}>
                            <Box>
                              <CustomTypography
                                variant="body1"
                                color="text.secondary"
                                fontWeight={500}
                                style={{ marginBottom: 0.5 }}
                              >
                                Created Date -{" "}
                                {order.created_date
                                  ? formatDate(order.created_date)
                                  : "-"}
                              </CustomTypography>
                            </Box>
                          </Grid2>

                          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                            <Box>
                              <CustomTypography
                                variant="body1"
                                color="text.secondary"
                                fontWeight={500}
                                style={{ marginBottom: 0.5 }}
                              >
                                Email Status -{" "}
                                {order.email_sent
                                  ? `Sent on ${formatDate(
                                    order.email_sent_date,
                                  )}`
                                  : "Pending"}
                              </CustomTypography>
                            </Box>
                          </Grid2>
                        </Grid2>

                        {order.cancellation_reason && (
                          <Box
                            sx={{
                              mt: 2,
                              p: 2,
                              backgroundColor: theme.palette.error.main + "10",
                              borderRadius: 1,
                              border: `1px solid ${theme.palette.error.main + "30"
                                }`,
                            }}
                          >
                            <CustomTypography
                              variant="body2"
                              color="error.main"
                              fontWeight={500}
                              sx={{ mb: 0.5 }}
                            >
                              Cancellation Reason
                            </CustomTypography>
                            <CustomTypography
                              variant="body2"
                              color="error.main"
                            >
                              {order.cancellation_reason}
                            </CustomTypography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  ),
                )}
              </Box>
            </Box>
          </>
        )}

      {/* Section Divider */}
      <Box
        sx={{
          height: 2,
          background: `linear-gradient(90deg, ${theme.palette.warning.main} 0%, ${theme.palette.info.main} 50%, ${theme.palette.primary.main} 100%)`,
          borderRadius: 1,
          mb: 4,
        }}
      />

      <>
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              mb: 3,
              p: 2,
              backgroundColor: theme.palette.purple.main + "08",
              borderRadius: 2,
              border: `2px solid ${theme.palette.purple.main}20`,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  backgroundColor: theme.palette.info.main,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ReceiptIcon sx={{ color: "white", fontSize: 20 }} />
              </Box>
              <Box>
                <CustomTypography
                  variant="heading5"
                  fontWeight={700}
                  color="info.main"
                >
                  Payments
                </CustomTypography>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <Chip
                label={`Total Paid Amount: INR ${totalPaidAmount.toLocaleString("en-IN")}`}
                size="small"
                color="info"
                variant="outlined"
                sx={{ fontWeight: 600, backgroundColor: "background.paper" }}
              />
              <AppButton
                label="Add Payment"
                startIcon={<AddIcon />}
                onClick={handleOpenCreateVendorPayment}
                disabled={
                  !userStore.hasPermission(
                    EmployeeRoles.ADD_EDIT_VENDOR_PAYMENTS,
                  ) ||
                  isPRCancelled ||
                  isAnyApprovalRejected
                }
                customStyles={{
                  borderRadius: "10px",
                  px: 2,
                  boxShadow: "0 8px 16px rgba(99, 102, 241, 0.20)",
                }}
              />
            </Box>
          </Box>

          <Box sx={{ display: "grid", gap: 2 }}>
            {!userStore.hasPermission(EmployeeRoles.VIEW_VENDOR_PAYMENTS) ? (
              <Box
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: `1px dashed ${theme.palette.divider}`,
                  textAlign: "center",
                  backgroundColor:
                    theme.palette.customColors.greyBodyBackground,
                }}
              >
                <CustomTypography variant="body1" fontWeight={600}>
                  You do not have access to view vendor payments.
                </CustomTypography>
              </Box>
            ) : vendorPaymentsStore.loading ? (
              <Box
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: `1px dashed ${theme.palette.divider}`,
                  textAlign: "center",
                }}
              >
                <CustomTypography variant="body2" color="text.secondary">
                  Loading vendor payments...
                </CustomTypography>
              </Box>
            ) : vendorPaymentsStore.payments.length === 0 ? (
              <Box
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: `1px dashed ${theme.palette.divider}`,
                  textAlign: "center",
                  backgroundColor:
                    theme.palette.customColors.greyBodyBackground,
                }}
              >
                <CustomTypography variant="body1" fontWeight={600}>
                  No vendor payments added yet
                </CustomTypography>
                <CustomTypography variant="body2" color="text.secondary">
                  Add advance or post-invoice payment entries for this purchase
                  request.
                </CustomTypography>
              </Box>
            ) : (
              vendorPaymentsStore.payments.map((payment) => (
                <Card
                  key={payment.id}
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.06)",
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        gap: 2,
                        flexWrap: "wrap",
                      }}
                    >
                      <Box sx={{ display: "grid", gap: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 1,
                            rowGap: 1,
                          }}
                        >
                          <CustomTypography
                            variant="heading6"
                            fontWeight={700}
                            color="primary.main"
                            sx={{ lineHeight: 1.2 }}
                          >
                            INR{" "}
                            {Number(payment.amount || 0).toLocaleString(
                              "en-IN",
                            )}
                          </CustomTypography>
                          <Chip
                            label={formatPaymentTimingLabel(
                              payment.payment_timing,
                            )}
                            color={
                              payment.payment_timing === "advance"
                                ? "warning"
                                : "info"
                            }
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                          <Chip
                            label={formatPaymentModeLabel(payment.payment_mode)}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 500 }}
                          />
                        </Box>
                        {payment.remarks?.trim() ? (
                          <CustomTypography
                            variant="body2"
                            color="text.secondary"
                          >
                            Remarks: {payment.remarks.trim()}
                          </CustomTypography>
                        ) : null}
                      </Box>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        {userStore.hasPermission(
                          EmployeeRoles.ADD_EDIT_VENDOR_PAYMENTS,
                        ) ? (
                          <IconButton
                            onClick={() => handleOpenEditVendorPayment(payment)}
                            sx={{
                              borderRadius: 1.5,
                              border: `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        ) : null}
                        {userStore.hasPermission(
                          EmployeeRoles.DELETE_VENDOR_PAYMENTS,
                        ) ? (
                          <IconButton
                            onClick={() =>
                              handleDeleteVendorPayment(payment.id)
                            }
                            sx={{
                              borderRadius: 1.5,
                              border: `1px solid ${theme.palette.error.main}30`,
                              color: theme.palette.error.main,
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        ) : null}
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        mt: 1.75,
                        pt: 1.75,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        flexWrap: "wrap",
                        gap: { xs: 1, sm: 2.5 },
                        alignItems: { sm: "center" },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                          minWidth: 0,
                        }}
                      >
                        <PersonOutlineIcon
                          sx={{
                            fontSize: 18,
                            color: "text.secondary",
                            opacity: 0.85,
                            flexShrink: 0,
                          }}
                        />
                        <CustomTypography
                          variant="caption"
                          color="text.secondary"
                          sx={{ lineHeight: 1.5 }}
                        >
                          <Box component="span" sx={{ fontWeight: 600 }}>
                            Created by
                          </Box>{" "}
                          <Box
                            component="span"
                            sx={{
                              fontWeight: 600,
                              color: "text.primary",
                            }}
                          >
                            {vendorPaymentActorLabel(
                              payment.created_by_name,
                              payment.created_by,
                            )}
                          </Box>
                          {payment.created_at ? (
                            <>
                              {" · "}
                              <Box component="span" sx={{ fontWeight: 500 }}>
                                {formatDate(payment.created_at)}
                              </Box>
                            </>
                          ) : null}
                        </CustomTypography>
                      </Box>
                      {vendorPaymentWasEdited(payment) ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            minWidth: 0,
                          }}
                        >
                          <UpdateIcon
                            sx={{
                              fontSize: 18,
                              color: "text.secondary",
                              opacity: 0.85,
                              flexShrink: 0,
                            }}
                          />
                          <CustomTypography
                            variant="caption"
                            color="text.secondary"
                            sx={{ lineHeight: 1.5 }}
                          >
                            <Box component="span" sx={{ fontWeight: 600 }}>
                              Last updated by
                            </Box>{" "}
                            <Box
                              component="span"
                              sx={{
                                fontWeight: 600,
                                color: "text.primary",
                              }}
                            >
                              {vendorPaymentActorLabel(
                                payment.updated_by_name,
                                payment.updated_by,
                              )}
                            </Box>
                            {payment.updated_at ? (
                              <>
                                {" · "}
                                <Box component="span" sx={{ fontWeight: 500 }}>
                                  {formatDate(payment.updated_at)}
                                </Box>
                              </>
                            ) : null}
                          </CustomTypography>
                        </Box>
                      ) : null}
                    </Box>
                  </CardContent>
                </Card>
              ))
            )}
          </Box>
        </Box>
      </>

      {/* Section Divider */}
      <Box
        sx={{
          height: 2,
          background: `linear-gradient(90deg, ${theme.palette.warning.main} 0%, ${theme.palette.info.main} 50%, ${theme.palette.primary.main} 100%)`,
          borderRadius: 1,
          mb: 4,
        }}
      />

      <>
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              mb: 3,
              p: 2,
              backgroundColor: theme.palette.purple.main + "08",
              borderRadius: 2,
              border: `2px solid ${theme.palette.purple.main}20`,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  backgroundColor: theme.palette.info.main,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ReceiptIcon sx={{ color: "white", fontSize: 20 }} />
              </Box>
              <Box>
                <CustomTypography
                  variant="heading5"
                  fontWeight={700}
                  color="info.main"
                >
                  Invoices
                </CustomTypography>
                <CustomTypography variant="body2" color="text.secondary">
                  {
                    purchaseRequestStore.purchaseRequestDetail
                      .purchase_order_invoices?.length
                  }{" "}
                  invoice
                  {purchaseRequestStore.purchaseRequestDetail
                    .purchase_order_invoices?.length !== 1
                    ? "s"
                    : ""}{" "}
                  available
                </CustomTypography>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              <Chip
                label="All"
                onClick={() => purchaseRequestStore.setPrFilter("ALL")}
                clickable
                color={
                  purchaseRequestStore.prFilter === "ALL"
                    ? "primary"
                    : "default"
                }
                variant={
                  purchaseRequestStore.prFilter === "ALL"
                    ? "filled"
                    : "outlined"
                }
                sx={{
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.15)",
                  },
                }}
              />
              <Chip
                label="Pending"
                onClick={() => purchaseRequestStore.setPrFilter("PENDING")}
                clickable
                color={
                  purchaseRequestStore.prFilter === "PENDING"
                    ? "warning"
                    : "default"
                }
                variant={
                  purchaseRequestStore.prFilter === "PENDING"
                    ? "filled"
                    : "outlined"
                }
                sx={{
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.15)",
                  },
                }}
              />
              <Chip
                label="Approved"
                onClick={() => purchaseRequestStore.setPrFilter("APPROVED")}
                clickable
                color={
                  purchaseRequestStore.prFilter === "APPROVED"
                    ? "success"
                    : "default"
                }
                variant={
                  purchaseRequestStore.prFilter === "APPROVED"
                    ? "filled"
                    : "outlined"
                }
                sx={{
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.15)",
                  },
                }}
              />
              <Chip
                label="Rejected"
                onClick={() => purchaseRequestStore.setPrFilter("REJECTED")}
                clickable
                color={
                  purchaseRequestStore.prFilter === "REJECTED"
                    ? "error"
                    : "default"
                }
                variant={
                  purchaseRequestStore.prFilter === "REJECTED"
                    ? "filled"
                    : "outlined"
                }
                sx={{
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.15)",
                  },
                }}
              />
            </Box>
          </Box>

          {purchaseRequestStore.purchaseRequestDetail.purchase_order_invoices &&
            purchaseRequestStore.purchaseRequestDetail.purchase_order_invoices
              .length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  mt: 3,
                  mb: 3,
                }}
              >
                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card
                      sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.06)",
                        p: 2,
                      }}
                    >
                      <CustomTypography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5, fontSize: "0.75rem" }}
                      >
                        Total Uploaded Invoices
                      </CustomTypography>
                      <CustomTypography
                        variant="heading6"
                        fontWeight={600}
                        color="primary.main"
                      >
                        {formatCurrency(invoiceStats.totalUploaded)}
                      </CustomTypography>
                    </Card>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card
                      sx={{
                        border: `1px solid ${theme.palette.success.main}`,
                        borderRadius: 2,
                        boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.06)",
                        p: 2,
                        backgroundColor: theme.palette.success.main + "05",
                      }}
                    >
                      <CustomTypography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5, fontSize: "0.75rem" }}
                      >
                        Approved
                      </CustomTypography>
                      <CustomTypography
                        variant="heading6"
                        fontWeight={600}
                        color="success.main"
                      >
                        {formatCurrency(invoiceStats.approved)}
                      </CustomTypography>
                    </Card>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card
                      sx={{
                        border: `1px solid ${theme.palette.warning.main}`,
                        borderRadius: 2,
                        boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.06)",
                        p: 2,
                        backgroundColor: theme.palette.warning.main + "05",
                      }}
                    >
                      <CustomTypography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5, fontSize: "0.75rem" }}
                      >
                        Pending
                      </CustomTypography>
                      <CustomTypography
                        variant="heading6"
                        fontWeight={600}
                        color="warning.main"
                      >
                        {formatCurrency(invoiceStats.pending)}
                      </CustomTypography>
                    </Card>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card
                      sx={{
                        border: `1px solid ${theme.palette.error.main}`,
                        borderRadius: 2,
                        boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.06)",
                        p: 2,
                        backgroundColor: theme.palette.error.main + "05",
                      }}
                    >
                      <CustomTypography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5, fontSize: "0.75rem" }}
                      >
                        Rejected
                      </CustomTypography>
                      <CustomTypography
                        variant="heading6"
                        fontWeight={600}
                        color="error.main"
                      >
                        {formatCurrency(invoiceStats.rejected)}
                      </CustomTypography>
                    </Card>
                  </Grid2>
                </Grid2>
              </Box>
            )}

          {purchaseRequestStore.purchaseRequestDetail.purchase_order_invoices &&
            purchaseRequestStore.purchaseRequestDetail.purchase_order_invoices
              .length > 0 && (
              <>
                {filteredInvoices.length > 0 ? (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    {filteredInvoices.map((invoice: PurchaseOrderInvoice) => {
                      return (
                        <Card
                          key={invoice.id}
                          sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 2,
                            boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.06)",
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                              boxShadow: "0px 4px 16px 0px rgba(0, 0, 0, 0.12)",
                              transform: "translateY(-2px)",
                            },
                          }}
                        >
                          <CardContent sx={{ p: 3 }}>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 3,
                                pb: 2,
                                borderBottom: `2px solid ${theme.palette.divider}`,
                                flexWrap: "wrap",
                                gap: 2,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 2,
                                  flexWrap: "wrap",
                                }}
                              >
                                <CustomTypography
                                  variant="heading6"
                                  fontWeight={700}
                                  color="primary.main"
                                >
                                  Invoice #{invoice.invoice_number}
                                </CustomTypography>
                                <Chip
                                  label={`PO #${invoice.po_id}`}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ fontWeight: 600 }}
                                />
                                {invoice.invoice_status && (
                                  <Chip
                                    label={invoice.invoice_status}
                                    size="small"
                                    color={
                                      invoice.invoice_status === "APPROVED"
                                        ? "success"
                                        : invoice.invoice_status === "REJECTED"
                                          ? "error"
                                          : "warning"
                                    }
                                    variant="filled"
                                    sx={{ fontWeight: 600 }}
                                  />
                                )}
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  alignItems: "center",
                                }}
                              >
                                <IconButton
                                  onClick={() =>
                                    setInvoiceDetailsPopup({
                                      open: true,
                                      invoice,
                                    })
                                  }
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    backgroundColor:
                                      theme.palette.info.main + "15",
                                    "&:hover": {
                                      backgroundColor:
                                        theme.palette.info.main + "25",
                                      transform: "scale(1.05)",
                                    },
                                  }}
                                  size="small"
                                >
                                  <InfoIcon
                                    sx={{
                                      color: "info.main",
                                      fontSize: 18,
                                    }}
                                  />
                                </IconButton>
                                {invoice.invoice_url && (
                                  <AppButton
                                    label="Download"
                                    variant="transparentPrimary"
                                    startIcon={<DownloadIcon />}
                                    onClick={() => {
                                      if (invoice.invoice_url) {
                                        window.open(
                                          invoice.invoice_url,
                                          "_blank",
                                        );
                                      }
                                    }}
                                    customStyles={{ minWidth: 130 }}
                                  />
                                )}
                              </Box>
                            </Box>

                            <Box
                              sx={{
                                mb: 2,
                                p: 1.5,
                                backgroundColor: theme.palette.info.main + "10",
                                borderRadius: 1,
                                border: `1px solid ${theme.palette.info.main}30`,
                              }}
                            >
                              <CustomTypography
                                variant="body2"
                                color="info.main"
                                fontWeight={500}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                {invoice.upload_invoice_admin_name
                                  ? `Invoice uploaded by admin: ${invoice.upload_invoice_admin_name}`
                                  : "Invoice uploaded by vendor Portal"}
                              </CustomTypography>
                            </Box>

                            <Grid2 container spacing={3} sx={{ mb: 3 }}>
                              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                                <Box
                                  sx={{
                                    p: 2.5,
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: 2,
                                    backgroundColor:
                                      theme.palette.background.paper,
                                    transition: "all 0.2s ease-in-out",
                                    minHeight: 86,
                                    "&:hover": {
                                      boxShadow:
                                        "0px 2px 8px 0px rgba(0, 0, 0, 0.1)",
                                      borderColor:
                                        theme.palette.primary.main + "40",
                                    },
                                  }}
                                >
                                  <CustomTypography
                                    variant="body2"
                                    color="text.secondary"
                                    fontWeight={500}
                                    sx={{ mb: 1 }}
                                  >
                                    Invoice Amount
                                  </CustomTypography>
                                  <CustomTypography
                                    variant="body1"
                                    fontWeight={600}
                                    color="primary.main"
                                  >
                                    ₹{invoice.invoice_amount || ""}
                                  </CustomTypography>
                                </Box>
                              </Grid2>

                              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                                <Box
                                  sx={{
                                    p: 2.5,
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: 2,
                                    backgroundColor:
                                      theme.palette.background.paper,
                                    transition: "all 0.2s ease-in-out",
                                    minHeight: 86,
                                    "&:hover": {
                                      boxShadow:
                                        "0px 2px 8px 0px rgba(0, 0, 0, 0.1)",
                                      borderColor:
                                        theme.palette.info.main + "40",
                                    },
                                  }}
                                >
                                  <CustomTypography
                                    variant="body2"
                                    color="text.secondary"
                                    fontWeight={500}
                                    sx={{ mb: 1 }}
                                  >
                                    Created Date
                                  </CustomTypography>
                                  <CustomTypography
                                    variant="body1"
                                    fontWeight={600}
                                    color="text.primary"
                                  >
                                    {invoice.created_date
                                      ? formatDate(invoice.created_date)
                                      : "-"}
                                  </CustomTypography>
                                </Box>
                              </Grid2>

                              {invoice.approve_invoice &&
                                invoice.approved_invoice_date && (
                                  <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Box
                                      sx={{
                                        p: 2.5,
                                        border: `1px solid ${theme.palette.success.main}40`,
                                        borderRadius: 2,
                                        backgroundColor:
                                          theme.palette.success.main + "08",
                                        transition: "all 0.2s ease-in-out",
                                        minHeight: 86,
                                        "&:hover": {
                                          boxShadow:
                                            "0px 2px 8px 0px rgba(0, 0, 0, 0.1)",
                                          borderColor:
                                            theme.palette.success.main + "60",
                                        },
                                      }}
                                    >
                                      <CustomTypography
                                        variant="body2"
                                        color="text.secondary"
                                        fontWeight={500}
                                        sx={{ mb: 1 }}
                                      >
                                        Approved Date
                                      </CustomTypography>
                                      <CustomTypography
                                        variant="body1"
                                        fontWeight={600}
                                        color="success.main"
                                      >
                                        {formatDate(
                                          invoice.approved_invoice_date,
                                        )}
                                      </CustomTypography>
                                    </Box>
                                  </Grid2>
                                )}

                              {invoice.reject_invoice &&
                                invoice.rejected_invoice_date && (
                                  <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Box
                                      sx={{
                                        p: 2.5,
                                        border: `1px solid ${theme.palette.error.main}40`,
                                        borderRadius: 2,
                                        backgroundColor:
                                          theme.palette.error.main + "08",
                                        transition: "all 0.2s ease-in-out",
                                        minHeight: 86,
                                        "&:hover": {
                                          boxShadow:
                                            "0px 2px 8px 0px rgba(0, 0, 0, 0.1)",
                                          borderColor:
                                            theme.palette.error.main + "60",
                                        },
                                      }}
                                    >
                                      <CustomTypography
                                        variant="body2"
                                        color="text.secondary"
                                        fontWeight={500}
                                        sx={{ mb: 1 }}
                                      >
                                        Rejected Date
                                      </CustomTypography>
                                      <CustomTypography
                                        variant="body1"
                                        fontWeight={600}
                                        color="error.main"
                                      >
                                        {formatDate(
                                          invoice.rejected_invoice_date,
                                        )}
                                      </CustomTypography>
                                    </Box>
                                  </Grid2>
                                )}

                              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                                <Box
                                  sx={{
                                    p: 2.5,
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: 2,
                                    backgroundColor:
                                      theme.palette.background.paper,
                                    transition: "all 0.2s ease-in-out",
                                    minHeight: 80,
                                    display: "flex",
                                    flexDirection: "column",
                                    "&:hover": {
                                      boxShadow:
                                        "0px 2px 8px 0px rgba(0, 0, 0, 0.1)",
                                      borderColor:
                                        theme.palette.info.main + "40",
                                    },
                                  }}
                                >
                                  <CustomTypography
                                    variant="body2"
                                    color="text.secondary"
                                    fontWeight={500}
                                    sx={{ mb: 1 }}
                                  >
                                    Payment Status
                                  </CustomTypography>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                    }}
                                  >
                                    <CustomTypography
                                      variant="body1"
                                      fontWeight={600}
                                      color="error.main"
                                    >
                                      {invoice.invoice_payment_status
                                        ? InvoicePaymentStatusFilters.find(
                                          (filter) =>
                                            filter.id ===
                                            invoice.invoice_payment_status,
                                        )?.value || "Not Set"
                                        : "Not Set"}
                                    </CustomTypography>

                                    <IconButton
                                      sx={{
                                        "&:hover": {
                                          backgroundColor: "transparent",
                                        },
                                        "&:active": {
                                          backgroundColor: "transparent",
                                        },
                                      }}
                                      onClick={(e) =>
                                        invoice.id &&
                                        handlePaymentStatusClick(e, invoice.id)
                                      }
                                    >
                                      <Icon sx={{ fontSize: "12px" }}>
                                        <img
                                          src={pencil_uderline}
                                          alt="edit-icon"
                                        />
                                      </Icon>
                                    </IconButton>
                                  </Box>
                                </Box>
                              </Grid2>
                            </Grid2>
                            {userStore.hasPermission(
                              EmployeeRoles.APPROVE_REJECT_PURCHASE_ORDER_INVOICES,
                            ) && (
                                <>
                                  {invoice.invoice_status !== "APPROVED" &&
                                    invoice.invoice_status !== "REJECTED" && (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          justifyContent: "flex-end",
                                          gap: 2,
                                          pt: 2,
                                          borderTop: `1px solid ${theme.palette.divider}`,
                                        }}
                                      >
                                        <AppButton
                                          label="Approve"
                                          variant="primary"
                                          onClick={() =>
                                            invoice.id &&
                                            handleApproveInvoice(invoice.id)
                                          }
                                          customStyles={{ minWidth: 120 }}
                                        />
                                        <AppButton
                                          label="Reject"
                                          variant="secondary"
                                          onClick={() =>
                                            invoice.id &&
                                            handleRejectInvoice(invoice.id)
                                          }
                                          customStyles={{ minWidth: 120 }}
                                        />
                                      </Box>
                                    )}

                                  {invoice.reject_invoice &&
                                    invoice.rejection_reason && (
                                      <Box
                                        sx={{
                                          mt: 3,
                                          p: 2.5,
                                          backgroundColor:
                                            theme.palette.error.main + "10",
                                          borderRadius: 2,
                                          border: `1px solid ${theme.palette.error.main + "30"
                                            }`,
                                          borderLeft: `4px solid ${theme.palette.error.main}`,
                                        }}
                                      >
                                        <CustomTypography
                                          variant="body2"
                                          color="error.main"
                                          fontWeight={600}
                                          sx={{ mb: 1 }}
                                        >
                                          Rejection Reason
                                        </CustomTypography>
                                        <CustomTypography
                                          variant="body1"
                                          color="error.main"
                                        >
                                          {invoice.rejection_reason}
                                        </CustomTypography>
                                      </Box>
                                    )}
                                </>
                              )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      p: 4,
                      textAlign: "center",
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      backgroundColor: theme.palette.background.paper,
                    }}
                  >
                    <CustomTypography
                      variant="body1"
                      color="text.secondary"
                      fontWeight={500}
                    >
                      No{" "}
                      {purchaseRequestStore.prFilter === "ALL"
                        ? ""
                        : purchaseRequestStore.prFilter.toLowerCase()}{" "}
                      invoices found
                    </CustomTypography>
                  </Box>
                )}
              </>
            )}
        </Box>

        <Box
          sx={{
            height: 2,
            background: `linear-gradient(90deg, ${theme.palette.info.main} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.success.main} 100%)`,
            borderRadius: 1,
            mb: 4,
          }}
        />
      </>

      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 3,
            p: 2,
            backgroundColor: theme.palette.info.main + "08",
            borderRadius: 2,
            border: `2px solid ${theme.palette.info.main}20`,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              backgroundColor: theme.palette.info.main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CalculateIcon sx={{ color: "white", fontSize: 20 }} />
          </Box>
          <Box>
            <CustomTypography
              variant="heading5"
              fontWeight={700}
              color="info.main"
            >
              Summary
            </CustomTypography>
            <CustomTypography variant="body2" color="text.secondary">
              Financial breakdown and calculations
            </CustomTypography>
          </Box>
        </Box>

        <Card
          sx={{
            border: `2px solid ${theme.palette.primary.main}20`,
            borderRadius: 3,
            boxShadow: "0px 8px 32px 0px rgba(0, 0, 0, 0.08)",
            overflow: "hidden",
          }}
        >
          <CardContent sx={{ p: 0 }}>
            {/* Calculation Details */}
            <Box sx={{ p: 4 }}>
              <Grid2 container spacing={3}>
                {/* Subtotal */}
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Box
                    sx={{
                      p: 3,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      backgroundColor: theme.palette.background.paper,
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        boxShadow: "0px 4px 12px 0px rgba(0, 0, 0, 0.1)",
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: theme.palette.info.main,
                          mr: 1.5,
                        }}
                      />
                      <CustomTypography
                        variant="body2"
                        color="text.secondary"
                        fontWeight={500}
                        sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                      >
                        Subtotal
                      </CustomTypography>
                    </Box>
                    <CustomTypography
                      variant="heading5"
                      fontWeight={700}
                      color="text.primary"
                    >
                      ₹{totals?.subtotal || 0}
                    </CustomTypography>
                    <CustomTypography variant="caption" color="text.secondary">
                      Before GST calculation
                    </CustomTypography>
                  </Box>
                </Grid2>

                {/* GST */}
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Box
                    sx={{
                      p: 3,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      backgroundColor: theme.palette.background.paper,
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        boxShadow: "0px 4px 12px 0px rgba(0, 0, 0, 0.1)",
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: theme.palette.warning.main,
                          mr: 1.5,
                        }}
                      />
                      <CustomTypography
                        variant="body2"
                        color="text.secondary"
                        fontWeight={500}
                        sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                      >
                        Total GST
                      </CustomTypography>
                    </Box>
                    <CustomTypography
                      variant="heading5"
                      fontWeight={700}
                      color="text.primary"
                    >
                      ₹{totals?.totalGst || 0}
                    </CustomTypography>
                    <CustomTypography variant="caption" color="text.secondary">
                      Tax amount calculated
                    </CustomTypography>
                  </Box>
                </Grid2>

                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Box
                    sx={{
                      p: 3,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      backgroundColor: theme.palette.background.paper,
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        boxShadow: "0px 4px 12px 0px rgba(0, 0, 0, 0.1)",
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: theme.palette.secondary.main,
                          mr: 1.5,
                          marginBottom: "0.5rem",
                          marginTop: "0.5rem",
                        }}
                      />
                      <CustomTypography
                        variant="body2"
                        color="text.secondary"
                        fontWeight={500}
                        style={{
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        Other Charges
                      </CustomTypography>
                    </Box>
                    <CustomTypography
                      variant="heading5"
                      fontWeight={700}
                      color="text.primary"
                    >
                      ₹{totals?.otherCharges || 0}
                    </CustomTypography>
                    <CustomTypography variant="caption" color="text.secondary">
                      Additional fees & charges
                    </CustomTypography>
                  </Box>
                </Grid2>

                {/* Grand Total */}
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Box
                    sx={{
                      p: 3,
                      border: `2px solid ${theme.palette.primary.main}`,
                      borderRadius: 2,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}08 0%, ${theme.palette.primary.main}15 100%)`,
                      position: "relative",
                      overflow: "hidden",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: theme.palette.primary.main,
                          mr: 1.5,
                        }}
                      />
                      <CustomTypography
                        variant="body2"
                        color="primary.main"
                        fontWeight={600}
                        sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                      >
                        Grand Total
                      </CustomTypography>
                    </Box>
                    <CustomTypography
                      variant="heading4"
                      fontWeight={800}
                      color="primary.main"
                    >
                      ₹{totals?.grandTotal || 0}
                    </CustomTypography>
                    <CustomTypography
                      variant="caption"
                      color="primary.main"
                      fontWeight={500}
                    >
                      Final amount to be paid
                    </CustomTypography>
                  </Box>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <Box
                    sx={{
                      p: 3,
                      border: `1px solid ${theme.palette.success.main}`,
                      borderRadius: 2,
                      background: `linear-gradient(135deg, ${theme.palette.success.main}08 0%, ${theme.palette.success.main}15 100%)`,
                      position: "relative",
                      overflow: "hidden",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        background: `linear-gradient(90deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: theme.palette.success.main,
                          mr: 1.5,
                        }}
                      />
                      <CustomTypography
                        variant="body2"
                        color="success.main"
                        fontWeight={600}
                        sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                      >
                        Remaining Amount
                      </CustomTypography>
                    </Box>
                    <CustomTypography
                      variant="heading4"
                      fontWeight={800}
                      color="success.main"
                    >
                      ₹{remainingAmountToBePaid.toLocaleString("en-IN")}
                    </CustomTypography>
                    <CustomTypography
                      variant="caption"
                      color="success.main"
                      fontWeight={500}
                    >
                      Unpaid Amount After Payments
                    </CustomTypography>
                  </Box>
                </Grid2>
              </Grid2>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <SingleInput
        open={purchaseRequestStore.editDialog.open}
        onClose={() => purchaseRequestStore.closeEditDialog()}
        heading={`Edit ${formatFieldName(
          purchaseRequestStore.editDialog.field || "",
        )}`}
        content={`Enter new ${formatFieldName(
          purchaseRequestStore.editDialog.field || "",
        )}`}
        type={
          purchaseRequestStore.editDialog.field === "gst_percentage"
            ? "select"
            : "number"
        }
        selectOptions={
          purchaseRequestStore.editDialog.field === "gst_percentage"
            ? GstPercentage
            : undefined
        }
        initialValue={
          purchaseRequestStore.editDialog.productId
            ? purchaseRequestStore.purchaseRequestDetail.products
              ?.find(
                (product) =>
                  product.product_id ===
                  purchaseRequestStore.editDialog.productId,
              )
              ?.[
              purchaseRequestStore.editDialog
                .field as keyof PurchaseRequestProduct
            ]?.toString() || ""
            : ""
        }
        onSubmit={(value) =>
          handleModalSubmit(
            purchaseRequestStore.editDialog.productId || 0,
            value,
            purchaseRequestStore.editDialog.field || "",
          )
        }
        required={purchaseRequestStore.editDialog.field ? true : false}
      />

      {/* Cancellation Reason Modal */}
      <ConfirmationPopup
        id={purchaseRequestStore.cancellationModal.poId || 0}
        open={purchaseRequestStore.cancellationModal.open}
        title="Cancel Purchase Order"
        content="Please provide a reason for cancelling this purchase order:"
        type="form"
        inputRequired={true}
        inputLabel="Cancellation Reason"
        inputType="text"
        onConfirm={handleCancellationSubmit}
        onCancel={handleCancellationClose}
      />

      {/* Status Change Confirmation Modal */}
      <ConfirmationPopup
        id={purchaseRequestStore.statusChangeModal.poId || 0}
        open={purchaseRequestStore.statusChangeModal.open}
        title="Update Purchase Order Status"
        content={`Are you sure you want to update the status to ${purchaseRequestStore.statusChangeModal.status}?`}
        type="standard"
        onConfirm={handleStatusChangeConfirm}
        onCancel={handleStatusChangeClose}
      />

      {/* Sourcing approve/reject confirmation */}
      <ConfirmationPopup
        id={purchaseRequestStore.purchaseRequestDetail?.id || 0}
        open={sourcingApprovePopupOpen}
        title="Approve Sourcing"
        content="Are you sure you want to approve the sourcing stage of this purchase request?"
        type="standard"
        onConfirm={(id) => handleApproveSourcingConfirm(id)}
        onCancel={handleApproveSourcingClose}
      />

      <ConfirmationPopup
        id={purchaseRequestStore.purchaseRequestDetail?.id || 0}
        open={sourcingRejectPopupOpen}
        title="Reject Sourcing"
        content="Please provide a reason for rejecting this purchase request sourcing:"
        type="form"
        inputRequired={true}
        inputLabel="Rejection Reason"
        inputType="text"
        onConfirm={(id, reason) =>
          handleRejectSourcingConfirm(id, reason || "")
        }
        onCancel={handleRejectSourcingClose}
      />

      {/* Finance approve/reject confirmation */}
      <ConfirmationPopup
        id={purchaseRequestStore.purchaseRequestDetail?.id || 0}
        open={financeApprovePopupOpen}
        title="Approve Finance"
        content="Are you sure you want to approve the finance stage of this purchase request?"
        type="standard"
        onConfirm={(id) => handleApproveFinanceConfirm(id)}
        onCancel={handleApproveFinanceClose}
      />

      <ConfirmationPopup
        id={purchaseRequestStore.purchaseRequestDetail?.id || 0}
        open={financeRejectPopupOpen}
        title="Reject Finance"
        content="Please provide a reason for rejecting this purchase request finance:"
        type="form"
        inputRequired={true}
        inputLabel="Rejection Reason"
        inputType="text"
        onConfirm={(id, reason) => handleRejectFinanceConfirm(id, reason || "")}
        onCancel={handleRejectFinanceClose}
      />

      {/* Swageazy approve/reject confirmation */}
      <ConfirmationPopup
        id={purchaseRequestStore.purchaseRequestDetail?.id || 0}
        open={swageazyApprovePopupOpen}
        title="Approve Swageazy"
        content="Are you sure you want to approve the swageazy stage of this purchase request?"
        type="standard"
        onConfirm={(id) => handleApproveSwageazyConfirm(id)}
        onCancel={handleApproveSwageazyClose}
      />

      <ConfirmationPopup
        id={purchaseRequestStore.purchaseRequestDetail?.id || 0}
        open={swageazyRejectPopupOpen}
        title="Reject Swageazy"
        content="Please provide a reason for rejecting this purchase request swageazy:"
        type="form"
        inputRequired={true}
        inputLabel="Rejection Reason"
        inputType="text"
        onConfirm={(id, reason) =>
          handleRejectSwageazyConfirm(id, reason || "")
        }
        onCancel={handleRejectSwageazyClose}
      />

      {/* Cancel PR confirmation */}
      <ConfirmationPopup
        id={purchaseRequestStore.purchaseRequestDetail?.id || 0}
        open={cancelPRPopupOpen}
        title="Cancel Purchase Request"
        content="Please provide a reason for cancelling this purchase request:"
        type="form"
        inputRequired={true}
        inputLabel="Cancellation Reason"
        inputType="text"
        onConfirm={(id, reason) => handleCancelPRConfirm(id, reason || "")}
        onCancel={handleCancelPRClose}
      />

      <ConfirmationPopup
        id={reactivatePoId || 0}
        open={reactivatePoPopupOpen}
        title="Reactivate Purchase Order"
        content="Reactivate this purchase order with the updated purchase request details?"
        type="standard"
        onConfirm={(poId) => handleReactivatePOConfirm(poId)}
        onCancel={handleReactivatePoClose}
      />

      {/* Purchase Vouchers Popup */}
      {selectedProductForVouchers && (
        <PurchaseVouchersPopup
          open={purchaseVouchersPopupOpen}
          onClose={() => {
            setPurchaseVouchersPopupOpen(false);
            setSelectedProductForVouchers(null);
          }}
          product={selectedProductForVouchers}
          pr_id={purchaseRequestStore.purchaseRequestDetail.id}
        />
      )}

      <ConfirmationPopup
        id={invoiceStore.approvePurchaseOrderInvoicePopup.invoiceId || 0}
        open={invoiceStore.approvePurchaseOrderInvoicePopup.open}
        title="Approve Invoice"
        content="Are you sure you want to approve this invoice?"
        type="standard"
        onConfirm={(id) => handleApproveInvoiceConfirm(id)}
        onCancel={handleApproveInvoiceClose}
      />

      <ConfirmationPopup
        id={invoiceStore.rejectPurchaseOrderInvoicePopup.invoiceId || 0}
        open={invoiceStore.rejectPurchaseOrderInvoicePopup.open}
        title="Reject Invoice"
        content="Please provide a reason for rejecting this invoice:"
        type="form"
        inputRequired={true}
        inputLabel="Rejection Reason"
        inputType="text"
        onConfirm={(id, reason) => handleRejectInvoiceConfirm(id, reason || "")}
        onCancel={handleRejectInvoiceClose}
      />

      <ConfirmationPopup
        id={vendorPaymentsStore.deleteState.paymentId || 0}
        open={vendorPaymentsStore.deleteState.open}
        title="Delete Vendor Payment"
        content="Are you sure you want to delete this payment entry?"
        type="standard"
        onConfirm={(id) => handleConfirmDeleteVendorPayment(id)}
        onCancel={() => vendorPaymentsStore.closeDeletePopup()}
      />

      <VendorPaymentPopup
        open={vendorPaymentsStore.popupState.open}
        onClose={() => vendorPaymentsStore.closePopup()}
        payment={vendorPaymentsStore.popupState.payment}
        onSave={handleSaveVendorPayment}
        loading={vendorPaymentsStore.saving}
        maxAllowedAmount={maxAllowedPaymentAmount}
      />

      <Popover
        open={Boolean(paymentStatusAnchor)}
        anchorEl={paymentStatusAnchor?.element}
        onClose={handlePaymentStatusClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            minWidth: 200,
            boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.15)",
            borderRadius: 2,
            mt: 0.5,
          },
        }}
      >
        <Box sx={{ py: 0.5 }}>
          {InvoicePaymentStatusFilters.map((status) => {
            const invoice =
              paymentStatusAnchor &&
              purchaseRequestStore.purchaseRequestDetail &&
              purchaseRequestStore.purchaseRequestDetail.purchase_order_invoices?.find(
                (inv) => inv.id === paymentStatusAnchor.invoiceId,
              );

            const isSelected =
              invoice?.invoice_payment_status !== undefined &&
              invoice.invoice_payment_status !== null &&
              invoice.invoice_payment_status === status.id;

            return (
              <MenuItem
                key={status.id}
                onClick={() => handlePaymentStatusSelect(status.id)}
                selected={isSelected || false}
                sx={{
                  py: 1.5,
                  px: 2,
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                  "&.Mui-selected": {
                    backgroundColor: theme.palette.primary.main + "15",
                    "&:hover": {
                      backgroundColor: theme.palette.primary.main + "25",
                    },
                  },
                }}
              >
                <ListItemText
                  primary={status.value}
                  primaryTypographyProps={{
                    fontSize: "0.875rem",
                    fontWeight: isSelected ? 600 : 500,
                    color: isSelected
                      ? theme.palette.primary.main
                      : theme.palette.text.primary,
                  }}
                />
              </MenuItem>
            );
          })}
        </Box>
      </Popover>

      {invoiceStore.invoiceUploadState.selectedPoId && (
        <InvoiceUploadDialog
          open={invoiceStore.invoiceUploadState.dialogOpen}
          onClose={handleCloseInvoiceUpload}
          onUpload={handleInvoiceUpload}
          products={purchaseRequestStore.purchaseRequestDetail?.products || []}
          invoices={
            purchaseRequestStore.purchaseRequestDetail?.purchase_order_invoices?.filter(
              (inv) =>
                inv.po_id === invoiceStore.invoiceUploadState.selectedPoId,
            ) || []
          }
          poId={invoiceStore.invoiceUploadState.selectedPoId}
          vendorId={invoiceStore.invoiceUploadState.vendorId || 0}
          isUploading={invoiceStore.invoiceUploadState.isUploading}
        />
      )}

      <Dialog
        open={invoiceDetailsPopup.open}
        onClose={() => setInvoiceDetailsPopup({ open: false, invoice: null })}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: `1px solid ${theme.palette.divider}`,
            pb: 2,
          }}
        >
          <CustomTypography variant="heading5" fontWeight={600}>
            Invoice #
            {invoiceDetailsPopup.invoice?.invoice_number ||
              invoiceDetailsPopup.invoice?.id}{" "}
            - Details
          </CustomTypography>
          <IconButton
            onClick={() =>
              setInvoiceDetailsPopup({ open: false, invoice: null })
            }
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          {invoiceDetailsPopup.invoice && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <Box
                sx={{
                  p: 2,
                  mt: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  backgroundColor: theme.palette.background.paper,
                }}
              >
                <CustomTypography
                  variant="body1"
                  fontWeight={600}
                  style={{ marginBottom: "0.5rem" }}
                >
                  Other Charges
                </CustomTypography>
                <CustomTypography
                  variant="heading6"
                  color="primary.main"
                  fontWeight={700}
                >
                  ₹
                  {invoiceDetailsPopup.invoice.other_charges !== undefined &&
                    invoiceDetailsPopup.invoice.other_charges !== null
                    ? Number(
                      invoiceDetailsPopup.invoice.other_charges,
                    ).toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                    : "0.00"}
                </CustomTypography>
              </Box>

              {invoiceDetailsPopup.invoice.invoice_products &&
                invoiceDetailsPopup.invoice.invoice_products.length > 0 ? (
                <Box>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{ fontWeight: 600, fontSize: "0.875rem" }}
                          >
                            Product Name
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ fontWeight: 600, fontSize: "0.875rem" }}
                          >
                            Quantity
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ fontWeight: 600, fontSize: "0.875rem" }}
                          >
                            Amount
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {invoiceDetailsPopup.invoice.invoice_products.map(
                          (product, index) => (
                            <TableRow key={product.id || index} hover>
                              <TableCell>
                                <CustomTypography
                                  variant="body2"
                                  fontWeight={500}
                                >
                                  {product.product_name || "-"}
                                </CustomTypography>
                              </TableCell>
                              <TableCell align="center">
                                <CustomTypography variant="body2">
                                  {product.quantity || "-"}
                                </CustomTypography>
                              </TableCell>
                              <TableCell align="center">
                                <CustomTypography variant="body2">
                                  {product.total_amount
                                    ? `₹${Number(
                                      product.total_amount,
                                    ).toLocaleString("en-IN", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}`
                                    : "-"}
                                </CustomTypography>
                              </TableCell>
                            </TableRow>
                          ),
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    py: 4,
                  }}
                >
                  <CustomTypography variant="body1" color="text.secondary">
                    No product details found for this invoice
                  </CustomTypography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: "flex-end" }}>
          <Button
            onClick={() =>
              setInvoiceDetailsPopup({ open: false, invoice: null })
            }
            variant="contained"
            sx={{
              minWidth: 100,
              textTransform: "none",
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default observer(PurchaseRequestDetail);
